<template>
    <div
      style="width:100%;height: 100%;"
      class="dashboard-widget-shell"
      :class="[{ 'cdb-dashboard-grid-widget': dashboardGridStyle }, 'cdb-widget--size-' + resolvedWidgetSize]"
      :aria-busy="refreshing ? 'true' : 'false'"
      ref="messageWrapper"
      @contextmenu="$emit('contextmenu', $event)">
        <!-- ECharts-only chart type but the engine fell back to Apex (chunk-load failure): there is
             no Apex builder, so show an explicit "engine required" state rather than silent blank
             chrome. Top-level (independent of chartReady / hasData) so it always wins. -->
        <div v-if="echartsOnlyUnavailable" class="cdb-widget-engine-required">
          <div class="ds-empty-state">
            <v-icon class="ds-empty-state__icon">mdi-chart-box-outline</v-icon>
            <div class="ds-empty-state__title">Chart engine required</div>
            <div class="ds-empty-state__description">This chart type needs the dashboard chart engine, which isn’t available right now. Refresh to try again.</div>
            <v-btn small text color="primary" class="ds-empty-state__action" @click="$emit('refreshGrid')">
              <v-icon small left>mdi-refresh</v-icon> Refresh
            </v-btn>
          </div>
        </div>
        <div v-else-if="!hasData && !refreshing" class="cdb-widget-empty">
          <template v-if="builderHeader">
            <!-- KPI-family empty (point #1): keep the full .cdb-kpi2 card anatomy (labeled header +
                 bottom-anchored meta) and render the VALUE slot as an explicit no-data state: a
                 plain hyphen (NEVER a coerced 0; the hasData gate keeps a real 0 in the populated
                 card) + an honest caption, instead of the generic chart empty body. The one
                 contextual filter-clear action stays; re-query is the widget's overlaid refresh
                 control. Neutral ink, no tone, no stale schema chip: there is no value to evaluate. -->
            <div v-if="isBuilderKpi" class="cdb-kpi2 cdb-kpi2--empty" :class="kpiSizeClass">
              <div class="cdb-kpi2__top">
                <span class="cdb-title-with-actions"><span class="cdb-kpi2__label">{{ message.title }}</span><slot name="title-actions"></slot></span>
                <v-tooltip v-if="filterNote" bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <span v-bind="attrs" v-on="on" class="cdb-filter-note" :class="`cdb-filter-note--${filterNote.kind}`">
                      <v-icon size="13" class="cdb-filter-note__icon">{{ filterNote.icon }}</v-icon>
                      <span class="cdb-filter-note__label">{{ filterNote.label }}</span>
                    </span>
                  </template>
                  <span>{{ filterNote.tooltip }}</span>
                </v-tooltip>
              </div>
              <div class="cdb-kpi2__val">
                <span class="cdb-kpi2__num cdb-kpi2__num--empty" aria-hidden="true">-</span>
              </div>
              <div class="cdb-kpi2__empty-caption" role="status">{{ emptyStateTitle }}</div>
              <div v-if="kpiEmptyAction" class="cdb-kpi2__empty-actions">
                <v-btn x-small outlined color="primary" @click="$emit(kpiEmptyAction.event)">{{ kpiEmptyAction.label }}</v-btn>
              </div>
              <div class="cdb-kpi2__meta">
                <span
                  v-for="(seg, i) in kpiFooterSegments"
                  :key="i"
                  :class="{ 'cdb-kpi2__meta-b': seg.bold }">{{ seg.text }}</span>
              </div>
            </div>
            <template v-else>
              <div
                v-if="!mini"
                class="cdb-widget-empty__head cdb-widget-head"
                :class="{ 'cdb-widget-head--dashboard-grid': dashboardGridStyle }">
                <div class="cdb-widget-empty__title-row">
                  <span class="text-h6 cdb-widget-title">
                    {{ message.title }}
                  </span>
                  <slot name="title-actions"></slot>
                </div>
                <span class="cdb-widget-sub">{{ widgetSubtitle }}</span>
              </div>

              <div class="cdb-widget-empty__body" role="status" aria-live="polite">
                <span class="cdb-widget-empty__icon" aria-hidden="true">
                  <v-icon>{{ emptyStateIcon }}</v-icon>
                </span>
                <strong>{{ emptyStateTitle }}</strong>
                <p>{{ emptyStateDescription }}</p>
                <div class="cdb-widget-empty__actions">
                  <v-btn
                    v-if="Number(activeFilterCount) > 0"
                    small
                    outlined
                    color="primary"
                    @click="$emit('clearGridFilters')">
                    Clear widget filters
                  </v-btn>
                  <v-btn
                    v-else-if="selectionEmptied"
                    small
                    outlined
                    color="primary"
                    @click="$emit('clearCrossFilter')">
                    Clear chart selections
                  </v-btn>
                  <v-btn
                    v-else-if="Number(dashboardFilterCount) > 0"
                    small
                    outlined
                    color="primary"
                    @click="$emit('openDashboardFilters')">
                    Review filters
                  </v-btn>
                  <v-btn small text color="primary" @click="$emit('refreshGrid')">
                    <v-icon small left>mdi-refresh</v-icon>
                    Refresh
                  </v-btn>
                </div>
              </div>
            </template>
          </template>

          <div v-else class="empty-data-section d-flex justify-center align-center">
            <i class="fa-solid fa-empty-set fa-xl"></i>
            <span>No data to display!</span>
          </div>
        </div>
        <!-- Full skeleton = INITIAL load only (point #8). During a refresh of a widget that has
             already shown data (isRefreshWithData) we skip it and fall through to the retained
             render below, which the scrim + progress strip overlay instead. -->
        <dashboard-widget-skeleton
          v-else-if="!chartReady && !isRefreshWithData"
          :widget-type="message && message.messageType"
          :show-header="!mini"
          :builder-header="!!builderHeader" />

        <div
          class="message-wrapper"
          :class="{ 'message-wrapper--dashboard-grid': dashboardGridStyle }"
          v-else>
            <v-row
              no-gutters
              ref="titleWrapper"
              v-if="!mini && !isBuilderKpi"
              :class="{
                'cdb-widget-head': builderHeader,
                'cdb-widget-head--dashboard-grid': dashboardGridStyle
              }">
                <v-col cols="12" class="d-flex justify-space-between ">
                    <!-- Showcase chart-card title. Non-builder consumers render the bare title.
                         The filter-visibility note (B2) joins the SAME left cluster after the
                         title, so the top-right stays clear for DashboardItem's overlaid controls. -->
                    <span class="cdb-widget-title-group d-inline-flex align-center" style="min-width: 0;">
                        <span :class="builderHeader ? 'cdb-widget-title' : 'text-h6'"> {{ message.title }}</span>
                        <slot name="title-actions"></slot>
                        <v-tooltip v-if="filterNote" bottom>
                            <template v-slot:activator="{ on, attrs }">
                                <span v-bind="attrs" v-on="on" class="cdb-filter-note ml-2" :class="`cdb-filter-note--${filterNote.kind}`">
                                    <v-icon size="13" class="cdb-filter-note__icon">{{ filterNote.icon }}</v-icon>
                                    <span class="cdb-filter-note__label">{{ filterNote.label }}</span>
                                </span>
                            </template>
                            <span>{{ filterNote.tooltip }}</span>
                        </v-tooltip>
                    </span>
                </v-col>
                <v-col>
                    <!-- Showcase muted sentence-case subtitle (source · by group · N records / metric)
                         from data already in hand; non-builder consumers keep today's subtitle. -->
                    <span v-if="builderHeader" class="cdb-widget-sub">{{ widgetSubtitle }}</span>
                    <span v-else class="   text--disabled"> {{ message.subTitle }}</span>
                </v-col>

            </v-row>

            <div
              v-if="message.messageType == 'datagrid'"
              class="table-wrapper"
              :class="{ 'table-wrapper--dashboard-grid': dashboardGridStyle }"
              ref="tableWrapper">
                <sql-view-data-grid
                  ref="sqlViewDataGrid"
                  :message="message"
                  :sqlViewConfig="parsedSqlViewConfig"
                  :globalFilters="globalFilters"
                  :filterSessionId="filterSessionId"
                  :sessionEmptyMatch="sessionEmptyMatch"
                  :selectionFilters="selectionFilters"
                  :frozen="frozen"
                  :widgetFilters="widgetFilters"
                  :dashboardStyle="dashboardGridStyle"
                  :widget-size="resolvedWidgetSize"
                  :activeFilterCount="activeFilterCount"
                  :refreshing="refreshing"
                  :isolation-claim="isolationClaim"
                   :is-claim-current="isClaimCurrent"
                   :claim-isolation="claimIsolation"
                   @openViewer="$emit('openViewer', $event)"
                   @spatialCapability="$emit('spatialCapability', $event)"
                   @openRowDetails="openRowDetails"
                   @selectionFilterRejected="$emit('selectionFilterRejected', $event)"
                   @openFilters="$emit('openGridFilters', $event)"
                  @refresh="$emit('refreshGrid')"
                  @clearFilters="$emit('clearGridFilters')">
                </sql-view-data-grid>
            </div>
            <div v-else-if="message.messageType == 'table'" class="table-wrapper" ref="tableWrapper">
                <v-data-table
                  :headers="headers"
                  ref="table"
                  :items="items"
                  :items-per-page="5"
                  :class="'elevation-1 '+tableClasses"
                  :item-class="tableRowClass"
                  tabindex="0"
                  aria-label="Data table. Use the up and down arrow keys to select a row, then press Enter to view details."
                  @click:row="selectTableRow"
                  @dblclick:row="openTableRowDetails"
                  @focus.native="ensureTableSelection"
                  @keydown.native.down.prevent="moveTableSelection(1)"
                  @keydown.native.up.prevent="moveTableSelection(-1)"
                  @keydown.native.enter.prevent="openSelectedTableRow">
                </v-data-table>
                <span class="ds-sr-only" role="status" aria-live="polite" aria-atomic="true">
                  {{ selectedTableAnnouncement }}
                </span>
            </div>
            <div v-else-if="message.messageType == 'count'" :style="(builderHeader || isGaugeKpi) ? { height: '100%' } : null">
                <!-- Gauge variant (sqlViewConfig.kpiVariant 'gauge'): a dial on the count/scalar path,
                     rendered via the async ECharts renderer (loaded by the isGaugeKpi watcher). Until
                     the chunk lands it shows the chart-shaped skeleton; a chunk-load failure flips
                     isGaugeKpi false so the plain count card below renders instead. -->
                <div v-if="isGaugeKpi" class="cdb-gauge" :class="kpiSizeClass">
                    <!-- Builder gauge title: the generic title row is suppressed for a builder count
                         widget (isBuilderKpi), so the gauge renders its own header with the SAME
                         structure/classes as the .cdb-kpi2 card (title + filter note);
                         kpiSizeClass keeps the tier label sizing identical. Gated on builderHeader so
                         a flag-off gauge (which still shows the generic title row) is not double-titled. -->
                    <div v-if="builderHeader" class="cdb-kpi2__top">
                        <span class="cdb-title-with-actions"><span class="cdb-kpi2__label">{{ message.title }}</span><slot name="title-actions"></slot></span>
                        <v-tooltip v-if="filterNote" bottom>
                            <template v-slot:activator="{ on, attrs }">
                                <span v-bind="attrs" v-on="on" class="cdb-filter-note" :class="`cdb-filter-note--${filterNote.kind}`">
                                    <v-icon size="13" class="cdb-filter-note__icon">{{ filterNote.icon }}</v-icon>
                                    <span class="cdb-filter-note__label">{{ filterNote.label }}</span>
                                </span>
                            </template>
                            <span>{{ filterNote.tooltip }}</span>
                        </v-tooltip>
                    </div>
                    <div class="cdb-gauge-wrap">
                        <component
                            :is="echartsComp"
                            v-if="echartsComp && gaugeEchartsOption"
                            :option="gaugeEchartsOption"
                            :widget-id="message.id"
                            style="width:100%;height:100%;" />
                        <dashboard-widget-skeleton
                            v-else
                            :widget-type="'count'"
                            :show-header="false" />
                    </div>
                </div>
                <!-- builder-v1 (flag-on): the ratified A+ .kpi2 card anatomy (kpi-card-options.html
                     :78-98) mapped to the widget — a labeled header row (title), the
                     big tabular value with the optional metric icon, and a hairline-topped meta
                     line (source · Function of column) bottom-anchored. This replaces the generic
                     head + bare number so the tile reads as a complete card, not a number stranded
                     in white space. The grid-item card (or the wizard preview stage) supplies the
                     flat surface + 12px inset; this block fills it (height:100%) and adds the
                     remaining showcase padding. A count carries no severity signal, so the value
                     takes theme-aware ink; never a GUID-hashed color (the removed anti-pattern). -->
                <div v-else-if="builderHeader" class="cdb-kpi2" :class="[kpiToneClass, kpiSizeClass]">
                    <div class="cdb-kpi2__top">
                        <span class="cdb-title-with-actions"><span class="cdb-kpi2__label">{{ message.title }}</span><slot name="title-actions"></slot></span>
                        <!-- Filter-visibility note (B2): joins the left cluster; kpi2__top keeps
                             the top-right clear for DashboardItem's overlaid controls. -->
                        <v-tooltip v-if="filterNote" bottom>
                            <template v-slot:activator="{ on, attrs }">
                                <span v-bind="attrs" v-on="on" class="cdb-filter-note" :class="`cdb-filter-note--${filterNote.kind}`">
                                    <v-icon size="13" class="cdb-filter-note__icon">{{ filterNote.icon }}</v-icon>
                                    <span class="cdb-filter-note__label">{{ filterNote.label }}</span>
                                </span>
                            </template>
                            <span>{{ filterNote.tooltip }}</span>
                        </v-tooltip>
                    </div>
                    <!-- Value row: number, then an optional unit, then a TRAILING status icon
                         (after the value, vertically centered) so the icon reads as a status
                         mark rather than decoration. The value and icon take the severity tone
                         color via the cdb-kpi2 modifier on the card. A neutral count keeps ink
                         for the value and muted ink for the icon. -->
                    <!-- KPI drill to list (listDrill): when the parent opts in, the value row becomes
                         the button that opens the side-panel list of the records behind the number.
                         Only the value row, so the card's own controls (About this number) are
                         never nested inside a button. -->
                    <v-tooltip bottom :disabled="!listDrillable" open-delay="300">
                        <template v-slot:activator="{ on, attrs }">
                            <component
                                :is="listDrillable ? 'button' : 'div'"
                                class="cdb-kpi2__val"
                                :class="{ 'cdb-kpi2__val--drill': listDrillable }"
                                v-bind="listDrillAttrs(attrs)"
                                v-on="listDrillListeners(on)">
                                <span v-if="metricIsKnown(count)" class="cdb-kpi2__num" :style="kpiValueStyle">{{ formatDashboardNumber(count) }}</span>
                                <span v-else class="text-body-1" role="status">Not available</span>
                                <span v-if="metricIsKnown(count) && kpiUnit" class="cdb-kpi2__unit">{{ kpiUnit }}</span>
                                <v-icon v-if="metricIsKnown(count) && kpiTrailingIcon" size="28" class="cdb-kpi2__icon">{{ kpiTrailingIcon }}</v-icon>
                                <v-icon v-if="listDrillable" size="20" class="cdb-kpi2__drill-icon" aria-hidden="true">mdi-format-list-bulleted</v-icon>
                            </component>
                        </template>
                        <span>{{ listDrillTooltip }}</span>
                    </v-tooltip>
                    <div v-if="!metricIsKnown(count)" class="text-caption">No known value for the included records. Open About this number for details.</div>
                    <!-- Schema status (builder-v1 'By schema' coloring, §4.4/§4.9): a labeled chip
                         shown only when the value can't be toned directly — a raw-color band match
                         (color dot + range label), a no-data / out-of-range miss (grey dot), or a
                         schema/bands failure (warn glyph). Absent for tone-backed matches (the
                         number itself carries the tone) and for every non-schema tile. -->
                    <div v-if="kpiSchemaChip" class="cdb-kpi2__status">
                        <span
                            class="cdb-kpi2__schema"
                            :class="{ 'cdb-kpi2__schema--alarm': kpiSchemaChip.alarming }">
                            <v-icon v-if="kpiSchemaChip.warn" size="13" class="cdb-kpi2__schema-warn">mdi-alert-outline</v-icon>
                            <span v-else class="cdb-kpi2__schema-dot" :style="{ backgroundColor: kpiSchemaChip.dot }"></span>
                            <span class="cdb-kpi2__schema-label">{{ kpiSchemaChip.label }}</span>
                        </span>
                    </div>
                    <!-- Footer: the authored footnote, or the auto source/aggregation meta when
                         blank, with digit-runs auto-bolded via segment spans (no v-html). -->
                    <div class="cdb-kpi2__meta">
                        <span
                            v-for="(seg, i) in kpiFooterSegments"
                            :key="i"
                            :class="{ 'cdb-kpi2__meta-b': seg.bold }">{{ seg.text }}</span>
                    </div>
                </div>
                <!-- flag-off / standalone: unchanged legacy count box (byte-identical). -->
                <div v-else class="pa-5 my-5" :style="{
                    'border': 'gray 1px solid',
                    'border-left': `${guidToHexColor(message.id)} 10px solid`,
                    'border-radius': '10px',
                    'width': '100%',
                } ">
                    <v-row no-gutters>
                        <v-col cols="12">
                            <span class="text-h4   font-weight-bold"> {{ displayCount }}</span>
                        </v-col>
                    </v-row>
                </div>
            </div>
            <div v-else class="chart-wrapper" ref="chartWrapper" style="width:100%;height: 100%;">
                <!-- Matrix widget whose saved aggregation can't produce an unambiguous category ×
                     series × value shape (Codex R1-2): a config-error state, never a wrong chart. -->
                <div v-if="matrixConfigError" class="ds-empty-state cdb-matrix-error">
                    <v-icon class="ds-empty-state__icon">mdi-chart-box-outline</v-icon>
                    <div class="ds-empty-state__title">Chart needs another dimension</div>
                    <div class="ds-empty-state__description">{{ matrixErrorMessage }}</div>
                </div>
                <template v-else>
                    <apex-charts v-if="chartEngine === 'apex'" ref="chart" style="width: 100%;height: 100%;"  :key="key" :type="message.messageType" :options="options" :series="message.series">

                    </apex-charts>
                    <component
                        :is="echartsComp"
                        v-else-if="echartsComp && echartsOption"
                        :option="echartsOption"
                        :selected-labels="chartSelectionLabels"
                        :widget-id="message.id"
                        style="width:100%;height:100%;"
                        @chart-select="onEchartsSelect" />
                    <!-- Engine flipped to ECharts on an already-ready widget but the chunk
                         is not loaded yet: retain the chart-shaped loading state. -->
                    <dashboard-widget-skeleton
                        v-else
                        :widget-type="message && message.messageType"
                        :show-header="false" />
                    <!-- Cap-sentinel notice (Codex R1-5): the matrix hit the baked row cap, so some
                         combinations may be missing. Unobtrusive, flat surface, no accent bar. -->
                    <div v-if="matrixTruncated" class="cdb-matrix-notice" role="status">
                        <v-icon size="13" class="cdb-matrix-notice__icon">mdi-alert-outline</v-icon>
                        <span>Showing a capped set — narrow your dimensions for the full picture.</span>
                    </div>
                </template>
            </div>

        </div>

        <!-- Refresh overlay (point #8). Two cases:
             1. isRefreshWithData: a re-query on a widget that already showed data. Keep the last
                render mounted and lay a restrained translucent scrim (blocks new datum clicks while
                the request settles) + a thin indeterminate progress strip on the top edge. Never a
                full skeleton (that reads as "the chart vanished").
             2. Otherwise (refreshing before any successful data): keep the full skeleton overlay so
                no stale/empty content leaks while the first result is still in flight. -->
        <template v-if="isRefreshWithData">
          <div class="cdb-refresh-scrim" aria-hidden="true"></div>
          <v-progress-linear indeterminate color="primary" height="2" class="cdb-refresh-bar" />
        </template>
        <dashboard-widget-skeleton
          v-else-if="refreshing && chartReady"
          class="dashboard-widget-skeleton--overlay"
          :widget-type="message && message.messageType"
          :show-header="!mini"
          :builder-header="!!builderHeader" />

        <!-- Stale/error notice (point #8 item 4): a FAILED refresh retains the last good data; show a
             small flat pill + retry (reuses the existing refresh pathway) instead of replacing the
             content with an empty/unfiltered view. Inert until DashboardItem forwards its hasError
             as :refresh-error. -->
        <div v-if="refreshError && !refreshing" class="cdb-stale-notice">
          <v-icon size="13" class="cdb-stale-notice__icon">mdi-alert-outline</v-icon>
          <span class="cdb-stale-notice__text">Showing last results. Refresh failed.</span>
          <v-btn text x-small color="primary" class="cdb-stale-notice__retry" @click="$emit('refreshGrid')">Retry</v-btn>
        </div>

        <!-- Visually-hidden live region: announces refresh + stale transitions to assistive tech. -->
        <span class="ds-sr-only" role="status" aria-live="polite">{{ refreshStatusText }}</span>

        <row-details-drawer
          v-if="!hasExternalRowDetailsConsumer"
          v-model="rowDetailsOpen"
          :row="rowDetailsRow"
          :config="parsedSqlViewConfig"
          :can-isolate="canIsolateDetailsRow"
          @isolate="isolateDetailsRow" />

    </div>
</template>
<script>
 /* eslint-disable  */
import { $uuidv4 } from '@/utils';
import { prepareChartData, prepareMatrixData, prepareMetricSeriesData, prepareComparisonData, sortPreparedDateSeries, preparedDatumValues, MATRIX_ERROR, COMPARISON_ERROR } from '@/features/Dashboard/service/chart-data.service';
import { buildChartOption, outcomeFill, SUPPORTED_TYPES, MATRIX_TYPES, ECHARTS_ONLY_TYPES, DUAL_AXIS_LINE, MIXED_COMPARISON } from '@/features/Dashboard/charts/echartsOptions';
import { buildGaugeOption } from '@/features/Dashboard/charts/gaugeOption';
import { resolveWidgetSize, widgetSizeClass, WIDGET_SIZE, LEGACY_FONT_SIZE_BY_WIDGET_SIZE } from '@/features/Dashboard/utils/widgetSize';
import { resolveSourceKey } from '@/features/Dashboard/utils/sourceKey';
import { resolveTagLink } from '@/features/Dashboard/utils/tagLink';
import { extractRenderedCategoryColors } from '@/features/Dashboard/utils/chartRenderedColors';
import { columnDisplayName } from '@/features/Dashboard/utils/columnLabels';
import { metricIsKnown, plainDashboardCopy, formatDashboardNumber } from '@/features/Dashboard/utils/dashboardReader';
import { COUNT_DISTINCT, FUNCTION_LABELS } from '@/features/Dashboard/utils/metricLabel';
import { LIST_DRILL_TOOLTIP } from '@/features/Dashboard/utils/listDrill';
import SqlViewDataGrid from '@/features/Dashboard/components/SqlViewDataGrid.vue';
import DashboardWidgetSkeleton from '@/features/Dashboard/components/DashboardWidgetSkeleton.vue';
import RowDetailsDrawer from '@/features/Dashboard/components/RowDetailsDrawer.vue';
import { log } from '@/utils/debugLogger';
// Coloring-schema engine (Phase 1, AVA-471): the builder count KPI resolves an optional
// `coloringSource: 'schema'` binding through the shared validator/engine (COLORING_SCHEMA_RESEARCH
// §4.1/§4.4/§4.6). Referenced ONLY by the builderHeader (flag-on) count branch, so flag-off /
// absent-coloringSource tiles never touch it.
import {
    COLORING_SOURCE,
    SCHEMA_KIND,
    RENDER_STATE,
    THEME,
} from '@/features/Dashboard/utils/coloringSchemas';
import { resolveSchema } from '@/features/Dashboard/utils/coloringSchemaEngine';
import appConfig from '@/../appConfig';

// Aggregation functions whose per-group results can be summed into a meaningful
// grand total. COUNT/SUM are additive; AVG/MIN/MAX are NOT — summing per-group
// averages/extrema yields garbage — and neither is COUNT_DISTINCT (one value can sit in
// several groups), so widgets using them show no grand total (subtitle "(N)" suppressed,
// donut center left as a clean hole). The wizard's full supported set is FUNCTION_LABELS.
const ADDITIVE_AGGREGATIONS = Object.freeze(['COUNT', 'SUM']);

// Display labels for SQL aggregate functions in the A+ KPI meta line: the wizard's own
// vocabulary (utils/metricLabel.js), so a new function needs one entry, not two.
const KPI_FUNCTION_LABELS = FUNCTION_LABELS;

// Valid KPI severity tones (builder-v1 A+ card). A value outside this set is neutral
// (no tint). Single source for the tone-string validation the kpiTone resolver repeats
// across its precedence branches.
const KPI_TONES = Object.freeze(['good', 'caution', 'critical']);
const KPI_STATUS_LABELS = Object.freeze({
    good: 'Good',
    caution: 'Caution',
    critical: 'Critical',
});
function isKpiTone(value) {
    return KPI_TONES.includes(value);
}

// A metric's aggregation function across the tolerated config shapes (saved widgets,
// legacy rows, and matrix metrics all spell it differently). Returns the raw token
// (e.g. 'COUNT') or a falsy value when the metric carries none / is absent.
function metricFn(metric) {
    return metric && (metric.function || metric.aggregate || metric.op || metric.type);
}

// Friendly "<Function> of <column>" caption for a metric, e.g. "Count of records" or
// "Average of Corrosion Rate": known functions map through KPI_FUNCTION_LABELS, the
// column resolves through displayNames, and a column-less metric reads as "records".
// Returns the bare column label when the metric has no function, '' when there is no
// metric. Callers that must stay blank for a column-less metric guard on the column first.
function metricLabel(metric, displayNames) {
    const names = displayNames || {};
    const rawCol = (metric && (metric.column || metric.name || metric.field)) || '';
    // 'RowIndex' is the synthetic row-number column behind a plain COUNT(*) widget; treat it as a
    // column-less count so the label reads "records" (e.g. "Count of records"), not "Count of RowIndex".
    const col = rawCol === 'RowIndex' ? '' : rawCol;
    const fn = metricFn(metric);
    const colLabel = col ? (names[col] || col) : (fn ? 'records' : '');
    if (!fn) return colLabel;
    // A distinct count reads "Distinct <column>", matching the wizard caption.
    if (String(fn).toUpperCase() === COUNT_DISTINCT && col) return `Distinct ${colLabel}`;
    const fnLabel = KPI_FUNCTION_LABELS[String(fn).toUpperCase()] || String(fn);
    return `${fnLabel} of ${colLabel}`;
}

export default {
    components: {
        SqlViewDataGrid,
        DashboardWidgetSkeleton,
        RowDetailsDrawer,
    },
    // `builderHeader` is passed ONLY by the custom-dashboard DashboardItem (flag-on) to opt
    // this widget into the showcase card-header pattern (title + uppercase source micro-label).
    // Absent/false for every other consumer (chatbot, wizard preview,
    // EditAIWidget) → they render byte-identical.
    // Selection round-trip (W2): `selectedLabels` is THIS widget's own selected server
    // values flowed down (null-preserving; null = the Unset bucket), and `crossFilterActive`
    // is true when ANY chart on the tab holds a selection (drives the cross-filter empty
    // state). `sessionEmptyMatch` = this widget's filter session was created with 0 tags (the
    // combined selection matched nothing, NOT a stale session); forwarded one hop into the
    // datagrid so it renders empty instead of a full unfiltered table (decision 10). The old
    // `legendFilter`/`legendFilterSource` prop chains are deleted — both unbound now, and the
    // single-source freeze concept is replaced by the dashboard's session model.
    // `refreshError` (point #8 item 4): forwarded from DashboardItem's own `hasError` so a FAILED
    // refresh (last-good data retained) can show a stale/retry notice instead of vanishing content.
    // Undefined for every consumer that does not forward it -> inert.
    // `selectionFilters` = the TRANSIENT same-table cross-filter predicates (dashboardSelectionScope);
    // forwarded to the grid, which merges them at payload-build time. Never widget filter state.
    // `listDrillable` (KPI drill to list): passed only by DashboardItem, in view mode, for a widget
    // whose config carries listDrill. The value row then becomes a button that emits `listDrill`;
    // `listDrillBusy` marks it busy while the list's ids are read. Absent everywhere else -> inert.
    props: ['message','mini','globalFilters','filterSessionId','selectionFilters','frozen','widgetFilters','builderHeader','dashboardGridStyle','activeFilterCount','dashboardFilterCount','refreshing','librarySchemas','selectedLabels','crossFilterActive','sessionEmptyMatch','refreshError','isolationClaim','isClaimCurrent','claimIsolation','listDrillable','listDrillBusy'],
    data() {
        return {
            headers: [],
            items: [],
            count: 0,
            title: '',
            key: '0',
            chartReady:false,
            resizeObserver: null,
            preparedLabels: [],
            options:{},
            // ECharts engine state (POC). `echartsComp` = async-loaded renderer;
            // `echartsUnavailable` flips true on chunk-load failure → forces apex.
            echartsUnavailable: false,
            echartsComp: null,
            // Refresh UX (point #8): once this widget has shown real data, a re-query keeps the
            // last render mounted and overlays a scrim + progress strip instead of a full skeleton.
            hasEverLoaded: false,
            // Latch: only treat `refreshing` as a refresh once we've OBSERVED it begin (false->true)
            // after mount. The widget can first paint with refreshing=true for one flush (the parent
            // sets data then clears loading in separate microtasks), and without this latch the scrim
            // would flash over that initial render instead of the clean skeleton.
            sawRefreshStart: false,
            selectedTableRow: null,
            rowDetailsOpen: false,
            rowDetailsRow: null,
        }
    },
    computed: {
        /**
         * Showcase chart-card subtitle (delta 5): muted sentence-case with middots,
         * e.g. "Anomaly Register · by Severity · 80 records". Built ONLY from data
         * the widget already holds (parsedSqlViewConfig + the loaded total) — no fetch,
         * no data-logic change. Each segment is added only when present, so a widget
         * without grouping/metrics still gets an honest source line.
         */
        widgetSubtitle() {
            if (this.message?.subTitle) return plainDashboardCopy(this.message.subTitle);
            const cfg = this.parsedSqlViewConfig;
            if (!cfg) return this.message?.subTitle || '';
            const parts = [];
            const source = cfg.tableDisplayName || cfg.tableName;
            if (source) parts.push(source);
            const agg = cfg.aggregation || {};
            const groupBy = Array.isArray(agg.groupBy) ? agg.groupBy : [];
            const displayNames = (cfg && cfg.columnDisplayNames) || {};
            const groupNames = groupBy.map(g => {
                const key = typeof g === 'string' ? g : (g.column || g.name || g.displayName || '');
                if (!key) return '';
                return displayNames[key] || (typeof g === 'object' && g.displayName) || key;
            }).filter(Boolean);
            if (groupNames.length) parts.push(`by ${groupNames.join(', ')}`);
            const metric = Array.isArray(agg.metrics) ? agg.metrics[0] : null;
            const fn = metricFn(metric);
            if (fn) {
                const n = this.widgetTotal;
                // Only additive aggregations (COUNT/SUM) get a grand total in parens;
                // AVG/MIN/MAX show the function word alone (summing them is garbage).
                const showTotal = this.isAdditiveAggregation && Number.isFinite(n);
                // Humanize the count metric: "count (110)" reads as "110 records". Non-count metrics
                // keep the function word (+ optional total for SUM), byte-identical to before.
                if (String(fn).toUpperCase() === 'COUNT' && showTotal) {
                    parts.push(`${n} records`);
                } else {
                    // Function word in reader form: "count distinct", never the raw "count_distinct".
                    const word = String(fn).toLowerCase().replace(/_/g, ' ');
                    parts.push(`${word}${showTotal ? ` (${n})` : ''}`);
                }
            }
            return parts.join(' · ') || (this.message?.subTitle || '');
        },
        /** Sum of the loaded chart series (the grouped total, e.g. 80) or null if not
         *  yet loaded — feeds the subtitle "(n)". Reuses the same prepare path. */
        widgetTotal() {
            // Matrix types carry raw dict rows (no .value/.label); prepareChartData would normalize
            // them to all-null and sum to a bogus 0, so the subtitle grand total is suppressed here.
            if (this.isMatrixType) return null;
            if (!this.message || !Array.isArray(this.message.content)) return null;
            const prepared = this.prepareSingleSeriesData();
            const values = (prepared && prepared.series) || [];
            if (!values.length) return null;
            return values.reduce((sum, v) => sum + (Number(v) || 0), 0);
        },
        // Effective raw widgetSize from the two persisted locations, precedence top-level ->
        // sqlViewConfig. The api silently drops unknown top-level fields, so a saved widget carries
        // its canonical widgetSize INSIDE sqlViewConfig; a synthetic/preview message may set it
        // top-level. Reuses the existing parsedSqlViewConfig parse (no second JSON.parse). May be
        // undefined/null/junk - callers validate (resolveWidgetSize is total; the Apex/table switches
        // fall through to the legacy fontSize when it is not an exact level).
        composedWidgetSize() {
            const top = this.message ? this.message.widgetSize : undefined;
            if (top !== undefined && top !== null) return top;
            const cfg = this.parsedSqlViewConfig;
            return cfg ? cfg.widgetSize : undefined;
        },
        tableClasses(){
            // Widget size wins when explicitly set (either persisted location); with no size on a
            // pre-feature widget we keep the legacy fontSize mapping verbatim (absent/unknown -> no
            // class, i.e. the v-data-table's default sizing) so existing tables stay byte-identical.
            // Not routed through resolveWidgetSize because that collapses "unset" to normal (a class),
            // which would re-size legacy tables that never carried a fontSize.
            switch (this.composedWidgetSize) {
                case WIDGET_SIZE.SMALL: return 'widget-table-small';
                case WIDGET_SIZE.NORMAL: return 'widget-table-medium';
                case WIDGET_SIZE.LARGE: return 'widget-table-large';
            }
            switch (this.message.fontSize) {
                case 0: return 'widget-table-small';
                case 1: return 'widget-table-medium';
                case 2: return 'widget-table-large';
                default: return undefined;
            }
        },
        selectedTableAnnouncement() {
            if (!this.selectedTableRow || !this.items.length) return '';
            const index = this.items.indexOf(this.selectedTableRow);
            if (index < 0) return '';
            return `Selected row ${index + 1} of ${this.items.length}. Press Enter to view details.`;
        },
        fontSize(){
            // Legacy Apex label px. Widget size wins when explicitly set (either persisted location);
            // otherwise the verbatim legacy fontSize switch (absent/unknown -> undefined, i.e. Apex's
            // own default) so pre-feature charts stay byte-identical. Same 10/16/20 scale as ECharts.
            switch (this.composedWidgetSize) {
                case WIDGET_SIZE.SMALL: return 10;
                case WIDGET_SIZE.NORMAL: return 16;
                case WIDGET_SIZE.LARGE: return 20;
            }
            switch (this.message.fontSize) {
                case 0: return 10;
                case 1: return 16;
                case 2: return 20;
                default: return undefined;
            }
        },
        // Canonical Widget size ('small' | 'normal' | 'large') for THIS widget, family-aware. Consumed
        // by the ECharts chart ctx, the KPI card class, the gauge scale and the datagrid :widget-size
        // prop. Three legacy behaviours, so a pre-feature widget reproduces its CURRENT look:
        //   - datagrid: fontSize was never applied to grids (meaningless noise), and today's fixed grid
        //     IS the large reference -> resolve LARGE (family flip) unless a widgetSize was picked.
        //   - count (KPI + gauge): also never read fontSize (fixed 37px / scale-1 dial) -> resolve
        //     NORMAL unless a widgetSize was picked; the stored fontSize is deliberately dropped by
        //     passing only the widgetSize, so a stray legacy number can't shrink/grow the card.
        //   - charts + the legacy v-data-table DID read fontSize -> the family-less path maps
        //     0/1/2 -> small/normal/large (byte-identical to today's px / table classes).
        resolvedWidgetSize() {
            const type = this.message && this.message.messageType;
            const widgetSize = this.composedWidgetSize;
            const fontSize = this.message ? this.message.fontSize : undefined;
            if (type === 'count') return resolveWidgetSize(widgetSize);
            const family = type === 'datagrid' ? 'datagrid' : undefined;
            return resolveWidgetSize({ widgetSize, fontSize }, { family });
        },
        // KPI card density modifier ('cdb-kpi2--small' | 'cdb-kpi2--large' | null). null at normal
        // keeps the base .cdb-kpi2 rules (37px value) verbatim - today's look.
        kpiSizeClass() {
            return widgetSizeClass(this.resolvedWidgetSize, 'cdb-kpi2');
        },
        // Gauge px scale for the density level (small 0.85 / normal 1 / large 1.2). Normal == 1 leaves
        // the dial's hardcoded 9/11/26px untouched (byte-identical).
        gaugeSizeScale() {
            switch (this.resolvedWidgetSize) {
                case WIDGET_SIZE.SMALL: return 0.85;
                case WIDGET_SIZE.LARGE: return 1.2;
                default: return 1;
            }
        },
        parsedSqlViewConfig() {
            if (!this.message || !this.message.sqlViewConfig) return null;
            try {
                return typeof this.message.sqlViewConfig === 'string'
                    ? JSON.parse(this.message.sqlViewConfig)
                    : this.message.sqlViewConfig;
            } catch (err) { return null; }
        },
        // Semantic source contract (#9) for THIS widget's data source, sent up with the
        // cross-filter emit so the dashboard can tell which selections combine. A persisted
        // key wins inside resolveSourceKey; otherwise it derives from tableName + saved
        // filters + facility. facilityId mirrors the render path (SqlViewDataGrid keys off
        // $route.params.id). null for a non-sql widget (no config) — harmless where unused.
        sourceKey() {
            const cfg = this.parsedSqlViewConfig;
            if (!cfg) return null;
            const facilityId = (this.$route && this.$route.params && this.$route.params.id) || null;
            return resolveSourceKey(cfg, facilityId);
        },
        // The dashboard flows this widget's own selection down as server VALUES (null =
        // the Unset bucket). Charts highlight by DATUM label, so map the Unset sentinel
        // back to its display label; every other value already equals its datum label.
        // Drives the ECharts visual selection and the Apex toggle baseline. [] = none.
        chartSelectionLabels() {
            const values = this.selectedLabels;
            return Array.isArray(values) ? values.map(v => (v == null ? 'Unset' : v)) : [];
        },
        hasData() {
            if (!this.message)
                return false;
            // datagrid widgets fetch their own data via the service layer
            if (this.message.messageType === 'datagrid' && this.message.sqlViewConfig) return true;
            var res=this.message.content;
            if (!res || !Array.isArray(res)|| res.length == 0) {
                return false;
            }

            return true;
        },
        // This widget returned no rows while OTHER charts on the tab hold selections and
        // this one holds none of its own — so its emptiness is (most likely) the chart
        // cross-filter narrowing it, not its own widget/dashboard filters. Drives the
        // contextual empty-state copy + the "Clear chart selections" action. crossFilterActive
        // is undefined outside the dashboard (no binding) → falsy → this stays false there.
        crossFilterEmptied() {
            return !!this.crossFilterActive && this.chartSelectionLabels.length === 0;
        },
        // This widget is empty because a chart selection legitimately matched nothing, NOT because
        // filters were skipped. Two sources: the tab has an active selection and this widget holds
        // none of its own (crossFilterEmptied), OR the parent flagged this widget's session as a
        // zero-tag empty-match (sessionEmptyMatch, decision 10). The empty-match arrives from the API
        // as filtersApplied=false + filterSkipReason='session-expired' (which would otherwise trip the
        // skip presentation), so sessionEmptyMatch is the authoritative "selection matched nothing"
        // signal — filters WERE applied, the selection just narrowed to zero.
        selectionEmptied() {
            return this.crossFilterEmptied || !!this.sessionEmptyMatch;
        },
        emptyStateIsFiltered() {
            return Number(this.activeFilterCount) > 0
                || Number(this.dashboardFilterCount) > 0
                || this.selectionEmptied;
        },
        emptyStateTitle() {
            // Skip precedence (genuine skip ONLY, skipReasonActive): the emptiness is NOT the filters
            // matching nothing (they were never applied to this source), so never say "No matching
            // data". An empty-match selection is excluded from this branch so it reads as "No matching
            // data" below, not as a filter skip.
            if (this.skipReasonActive) return 'Dashboard filters not applied';
            return this.emptyStateIsFiltered ? 'No matching data' : 'No data yet';
        },
        emptyStateDescription() {
            if (this.skipReasonActive) {
                return this.message.filterSkipReason
                    || 'This data source has no tag column, so dashboard filters were not applied. It currently has no records to show.';
            }
            if (Number(this.activeFilterCount) > 0) {
                return 'No records match this widget\'s filters. Clear them or adjust the criteria.';
            }
            if (this.selectionEmptied) {
                return 'No data matches the current chart selections. Clear them to restore the full view.';
            }
            if (Number(this.dashboardFilterCount) > 0) {
                return 'No records match the active dashboard filters. Review the filters to widen the scope.';
            }
            const cfg = this.parsedSqlViewConfig;
            const source = cfg && (cfg.tableDisplayName || cfg.tableName);
            return source
                ? `No records are currently available from ${source}.`
                : 'This widget returned no records. Refresh to check for newly available data.';
        },
        emptyStateIcon() {
            if (this.skipReasonActive) return 'mdi-filter-off-outline';
            const type = this.message && this.message.messageType;
            if (type === 'datagrid' || type === 'table') return 'mdi-table-off';
            if (type === 'count') return 'mdi-counter';
            return 'mdi-chart-box-outline';
        },
        // ── Refresh loading UX (point #8) ────────────────────────────────────────
        // A re-query on a widget that has ALREADY shown data: keep the last successful render and
        // overlay a restrained scrim + top progress strip (never a full skeleton, which reads as
        // "the chart vanished"). Gated on the sawRefreshStart latch so the brief mount-time
        // refreshing=true window can't flash the scrim over the first paint. hasEverLoaded is the
        // "prior successful data" notion: a first load that never got data keeps today's full
        // skeleton overlay instead (no stale content to preserve).
        isRefreshWithData() {
            return !!this.refreshing && this.hasEverLoaded && this.sawRefreshStart;
        },
        // Screen-reader status for the widget body's live region, announced on change: a settling
        // refresh, or a failed refresh whose last-good data is being retained. '' the rest of the time.
        refreshStatusText() {
            if (this.isRefreshWithData) return 'Refreshing data';
            if (this.refreshError && !this.refreshing) return 'Refresh failed. Showing last results.';
            return '';
        },
        // The ONE contextual recovery action for an empty builder KPI (point #1): mirrors the generic
        // empty-state action precedence (widget filters -> chart selections -> dashboard filters) but
        // drops the always-on Refresh: DashboardItem already overlays a refresh control on every
        // widget, and a second button risks overflowing the compact KPI tile. null when none applies.
        kpiEmptyAction() {
            if (Number(this.activeFilterCount) > 0) {
                return { label: 'Clear widget filters', event: 'clearGridFilters' };
            }
            if (this.selectionEmptied) {
                return { label: 'Clear chart selections', event: 'clearCrossFilter' };
            }
            if (Number(this.dashboardFilterCount) > 0) {
                return { label: 'Review filters', event: 'openDashboardFilters' };
            }
            return null;
        },
        // ── Filter-visibility state (Package B / B2) ─────────────────────────────
        // The API bubbles filtersApplied=false + filterSkipReason on the widget VM (top-level,
        // siblings of content) when a source has no tag column, so dashboard filters can't touch
        // it. These fields ride on `message` (this.widget in DashboardItem), consumed here the
        // same way message.content / message.series / message.legend already are.
        /** Strict === false only: absent (old responses, chatbot/wizard consumers) → NOT skipped. */
        filtersSkipped() {
            return !!this.message && this.message.filtersApplied === false;
        },
        // The GENUINE filter-skip presentation gate (title / description / icon / header chip). A
        // zero-tag empty-match arrives as filtersApplied=false too, but there filters WERE applied and
        // the selection simply matched nothing, so it must NOT read as a skip: exclude it here and let
        // selectionEmptied drive the "No matching data" / "Clear chart selections" copy instead. A real
        // no-tag-column source cannot create a session at all, so it never sets sessionEmptyMatch; the
        // two states are mutually exclusive and genuine skips (degraded, real expiry) are unaffected.
        skipReasonActive() {
            return this.filtersSkipped && !this.sessionEmptyMatch;
        },
        /** DashboardItem forwards globalFilters ONLY while the dashboard filter session is
         *  degraded (server-side creation failed → widgets query via the raw fallback). Its
         *  presence is the per-widget "running degraded" signal (B1.6). */
        filtersRunningDegraded() {
            return !!(this.globalFilters && Object.keys(this.globalFilters).length);
        },
        /** The single subtle header note. A source that can't use filters at all (skip) takes
         *  precedence over the degraded-session hint (filters DID apply, just via the raw path).
         *  Null → no note. Copy carries no em dashes; the reason rides in the tooltip. */
        filterNote() {
            if (this.skipReasonActive) {
                return {
                    kind: 'skip',
                    icon: 'mdi-filter-off-outline',
                    label: 'Dashboard filters not applied to this source',
                    tooltip: this.message.filterSkipReason
                        || 'This data source has no tag column, so dashboard filters cannot narrow it.',
                };
            }
            if (this.filtersRunningDegraded) {
                return {
                    kind: 'degraded',
                    icon: 'mdi-progress-clock',
                    label: 'Degraded filters',
                    tooltip: 'The dashboard filter session is unavailable, so results are still filtered via a fallback.',
                };
            }
            return null;
        },
        // Resolve the render engine for THIS widget. ECharts is the adopted engine for custom
        // dashboards; Apex is the automatic fail-safe for the LEGACY types it can render.
        // Total precedence, apex is the fail-safe:
        //   runtime chunk-load failure → apex
        //   not engine-capable (no sqlViewConfig: AI/chat widget) → apex   (Risk 3 / R2-1)
        //   messageType has no ECharts builder → apex   (partial-coverage safe)
        //   else env default (dashboardChartEngineDefault; 'echarts' now, malformed → apex)
        // Apex does NOT cover the ECharts-only types (ECHARTS_ONLY_TYPES — the matrix charts have no
        // Apex builder). When one of those falls back to Apex the widget shows an explicit "engine
        // required" state (see echartsOnlyUnavailable), never silent blank chrome.
        chartEngine() {
            if (this.echartsUnavailable) return 'apex';
            if (!this.parsedSqlViewConfig) return 'apex';
            if (!SUPPORTED_TYPES.includes(this.message && this.message.messageType)) return 'apex';
            return appConfig.dashboardChartEngineDefault === 'echarts' ? 'echarts' : 'apex';
        },
        // First groupBy column NAME (schema, not data) for THIS widget, '' when
        // ungrouped. Handed to the chart builder so the pie RAG-vs-severity color
        // decision keys on the column (stable) instead of the returned label set —
        // which changes when a severity level is filtered out. Mirrors widgetSubtitle's
        // g→key extraction so string and object groupBy shapes both resolve.
        groupByColumn() {
            const cfg = this.parsedSqlViewConfig;
            const groupBy = cfg && Array.isArray(cfg.aggregation?.groupBy) ? cfg.aggregation.groupBy : [];
            const first = groupBy[0];
            if (!first) return '';
            return typeof first === 'string' ? first : (first.column || first.name || first.displayName || '');
        },
        groupByDataType() {
            const value = this.parsedSqlViewConfig?.columnTypes?.[this.groupByColumn];
            return typeof value === 'string' ? value : (value?.dataType || value?.type || '');
        },
        // Optional per-widget donut/pie center noun (delta 1), read straight from the
        // widget's saved sqlViewConfig.centerLabel (e.g. "anomalies", "CUI rows").
        // Data-driven only — no builder UI yet. null when absent/blank, so the chart
        // builder keeps its neutral 'total' default. Consumed ONLY by the showcase
        // donut center — see echartsOptions buildChartOption.
        centerLabel() {
            const label = this.parsedSqlViewConfig && this.parsedSqlViewConfig.centerLabel;
            return (typeof label === 'string' && label.trim()) ? label.trim() : null;
        },
        // Optional KPI metric icon (builder-v1 A+ card only), read from the widget's
        // saved sqlViewConfig.icon (an MDI name; '' = none). Rendered small and
        // secondary-colored beside the big number so it labels the metric. Consumed
        // ONLY by the builderHeader count branch — the legacy v-else count box ignores
        // it, so flag-off count tiles stay byte-identical. null when absent/blank.
        kpiIcon() {
            const cfg = this.parsedSqlViewConfig;
            const icon = cfg && typeof cfg.icon === 'string' ? cfg.icon.trim() : '';
            return icon || null;
        },
        // Optional short unit (builder-v1 A+ card only), e.g. "yr", read from the saved
        // sqlViewConfig.unit. Rendered small and muted on the value's baseline, after the
        // number. null when absent/blank so the unit span does not render. Consumed ONLY by
        // the builderHeader count branch, so flag-off count tiles stay byte-identical.
        kpiUnit() {
            const cfg = this.parsedSqlViewConfig;
            const unit = cfg && typeof cfg.unit === 'string' ? cfg.unit.trim() : '';
            return unit || null;
        },
        // ── Coloring-schema consumption (builder count KPI, §4.1/§4.4/§4.6) ──────────────
        // Every computed below is read ONLY from kpiTone's explicit-source branch or from inside
        // the v-if="builderHeader" kpi2 block, so a flag-off / absent-coloringSource tile never
        // evaluates them. Phase 1 built-ins are all STATIC, so this path touches only the pure
        // engine (no store, no route, no async bands).

        // Parsed, validated coloring source: 'static' | 'direction' | 'schema', or '' when the key
        // is absent (→ kpiTone runs the shipped legacy evaluator, byte-identically).
        kpiColoringSource() {
            const cfg = this.parsedSqlViewConfig;
            const src = cfg && typeof cfg.coloringSource === 'string' ? cfg.coloringSource.trim().toLowerCase() : '';
            return (src === COLORING_SOURCE.STATIC || src === COLORING_SOURCE.DIRECTION || src === COLORING_SOURCE.SCHEMA)
                ? src : '';
        },
        // The widget's schemaSelection blob ({ kind, schemaId, valueUnit, definition }, §4.3), or null.
        kpiSchemaSelection() {
            const cfg = this.parsedSqlViewConfig;
            const sel = cfg && cfg.schemaSelection;
            return sel && typeof sel === 'object' ? sel : null;
        },
        // Engine theme selector — dark hex vs light hex for a resolved {light,dark} pair (§4.8).
        kpiTheme() {
            return this.$vuetify && this.$vuetify.theme && this.$vuetify.theme.dark ? THEME.DARK : THEME.LIGHT;
        },
        // Resolve the KPI value through its schema → one §4.4 outcome, or null when the KPI is not
        // in schema mode. Phase 1 built-ins are all STATIC (self-contained ranges/entries — no
        // facility bands, no async), and inline selections flow through identically; the engine
        // resolves builtin | inline the same way, so this consumer never branches on kind.
        // PURE: no dispatch, no logging here (side effects live in the watcher).
        kpiSchemaOutcome() {
            if (!this.isBuilderKpi) return null;
            if (this.kpiColoringSource !== COLORING_SOURCE.SCHEMA) return null;
            const selection = this.kpiSchemaSelection;
            if (!selection) return null;
            // Reference loading gate: a 'reference' selection resolves from the tab's schema library,
            // which loads async. While librarySchemas is null (still loading, or a non-board consumer
            // that never passes it) render neutral (no resolve) so we never flash schemaUnavailable —
            // the same seam as the old bands loading-gate. An empty [] (loaded, no schemas) is NOT
            // gated: a dangling reference then legitimately resolves schemaUnavailable.
            if (selection.kind === SCHEMA_KIND.REFERENCE && this.librarySchemas == null) return null;
            // Seam a: pass the RAW measure + its unit; the engine converts internally. Pre-converting
            // AND passing valueUnit would double-convert (-0.6 yr -> -7.2 -> -86.4). librarySchemas
            // feeds reference resolution (ignored for builtin/inline; content-keyed, no version).
            const lookup = resolveSchema(selection, { theme: this.kpiTheme, librarySchemas: this.librarySchemas });
            return lookup(this.count, { valueUnit: selection.valueUnit });
        },
        // Template-facing status chip — FAILURE STATES ONLY. A matched value (tone OR raw color) is
        // shown by coloring the number itself (tone class / kpiValueStyle), never a chip. So this is
        // null for every matched outcome and every non-schema path; it renders only for noData /
        // unmappedValue (grey dot + label) and schemaUnavailable / loadError (warn glyph + label).
        // §4.9: every chip carries a text label, never a color-only dot.
        kpiSchemaChip() {
            const outcome = this.kpiSchemaOutcome;
            if (!outcome || outcome.state === RENDER_STATE.MATCHED) return null;
            const warn = outcome.state === RENDER_STATE.SCHEMA_UNAVAILABLE || outcome.state === RENDER_STATE.LOAD_ERROR;
            return {
                label: outcome.label,
                dot: warn ? null : outcome.resolvedColor,
                warn,
                alarming: outcome.state === RENDER_STATE.SCHEMA_UNAVAILABLE,
            };
        },
        // Inline color for the KPI value when a RAW-COLOR schema match applies — the engine's
        // theme-resolved, contrast-safe `outcome.textColor` (§4.9). Tone matches are handled by the
        // tone class (so guard on `!tone`); a failure/neutral outcome, or an absent textColor (the
        // until-engine-lands + defensive path), leaves the number in ink. null on every non-schema
        // path, so flag-off / absent-key tiles bind no inline style.
        kpiValueStyle() {
            const outcome = this.kpiSchemaOutcome;
            if (outcome && outcome.state === RENDER_STATE.MATCHED && !outcome.tone && outcome.textColor) {
                return { color: outcome.textColor };
            }
            return null;
        },
        // Dynamic KPI status (builder-v1 A+ card only). BI-ratified precedence:
        //   thresholds over the value  ->  legacy manual tone (fixed colour)  ->  neutral ink.
        // The thresholds are absolute bands (Grafana-steps / Datadog warning+critical), so the
        // status recomputes client-side on every fetch: this reads `this.count` — the SAME
        // reactive value the card shows (.cdb-kpi2__num) — so a re-query that changes count
        // re-runs this computed and repaints the tone. Consumed ONLY by the builderHeader count
        // branch, so flag-off count tiles (which never read kpiTone) stay byte-identical.
        // Returns 'good' | 'caution' | 'critical', or '' for neutral.
        kpiTone() {
            const cfg = this.parsedSqlViewConfig || {};
            // Explicit coloring source (builder-v1 schema feature, §4.1). An ABSENT coloringSource
            // falls through to the shipped evaluator below, byte-identically; only an explicit
            // source activates a new branch.
            const coloringSource = this.kpiColoringSource;
            if (coloringSource === COLORING_SOURCE.STATIC) {
                // 'static' = fixed manual tone; the value never changes it (thresholds ignored).
                const fixed = typeof cfg.tone === 'string' ? cfg.tone.trim().toLowerCase() : '';
                return isKpiTone(fixed) ? fixed : '';
            }
            if (coloringSource === COLORING_SOURCE.SCHEMA) {
                // 'schema': a tone-backed match drives the tone class (exactly as static/direction do).
                // A raw-color match carries no tone — its value is colored inline via kpiValueStyle
                // (outcome.textColor); a failure state keeps neutral ink + a chip.
                const outcome = this.kpiSchemaOutcome;
                return (outcome && outcome.state === RENDER_STATE.MATCHED && outcome.tone) ? outcome.tone : '';
            }
            // 'direction' OR absent coloringSource → the shipped thresholds→tone→neutral evaluator,
            // unchanged.
            // Step 0 (non-negotiable): guard null/undefined BEFORE numeric coercion. Number(null)
            // is 0 — a lower-is-worse tile (critical <= 0) would flash CRITICAL before data lands
            // if we compared the coerced 0 — so the raw null/undefined check must come first.
            if (this.count === null || this.count === undefined) return '';
            const value = Number(this.count);
            if (Number.isNaN(value)) return '';
            const thresholds = cfg.thresholds;
            const rules = thresholds && Array.isArray(thresholds.rules) ? thresholds.rules : null;
            if (rules && rules.length) {
                // Never trust stored order: evaluate most-severe-first so a value that clears both
                // boundaries reports critical, not caution. Op is gte/lte only (single source of
                // truth: the editor's direction sets it — higherIsWorse->gte, lowerIsWorse->lte).
                const severity = { critical: 3, caution: 2, good: 1 };
                const compare = (op, boundary) => {
                    const b = Number(boundary);
                    if (Number.isNaN(b)) return false;
                    if (op === 'gte') return value >= b;
                    if (op === 'lte') return value <= b;
                    return false;
                };
                const match = [...rules]
                    .sort((a, b) => (severity[b && b.tone] || 0) - (severity[a && a.tone] || 0))
                    .find(r => r && compare(r.op, r.value));
                if (match) return match.tone;
                const fallback = thresholds.default;
                return isKpiTone(fallback) ? fallback : '';
            }
            // No thresholds: fall back to the legacy manual tone (fixed colour), one priority below.
            const tone = typeof cfg.tone === 'string' ? cfg.tone.trim().toLowerCase() : '';
            return isKpiTone(tone) ? tone : '';
        },
        // One aggregate status presentation for Spatial Focus. It is intentionally independent of
        // row encoding: a Number/KPI evaluates ONE displayed measure, so every matching target gets
        // the same fill and the resolver scope must not invent a physical status column. A matched
        // schema outcome is kept whole so its authored label and fill travel atomically; threshold
        // and fixed-tone KPIs synthesize the equivalent outcome. `outcomeFill` is the chart-fill
        // authority — `textColor` is contrast-safe KPI ink and is never a 3D material.
        kpiSpatialPresentation() {
            if (!this.isBuilderKpi) return null;
            const schemaOutcome = this.kpiSchemaOutcome;
            const matchedSchema = schemaOutcome && schemaOutcome.state === RENDER_STATE.MATCHED
                ? schemaOutcome
                : null;
            const fillOutcome = matchedSchema || (this.kpiTone ? { tone: this.kpiTone } : null);
            if (!fillOutcome) return null;

            const color = outcomeFill(fillOutcome, this.kpiTheme);
            const schemaLabel = matchedSchema && typeof matchedSchema.label === 'string'
                ? matchedSchema.label.trim()
                : '';
            const label = schemaLabel || KPI_STATUS_LABELS[this.kpiTone] || '';
            if (!color || !label) return null;

            return {
                kind: 'constant',
                color,
                label,
                displayName: 'KPI status',
            };
        },
        // Card modifier class that tints the value + trailing icon in the tone color; null when
        // neutral (value stays ink, icon stays muted ink).
        kpiToneClass() {
            return this.kpiTone ? `cdb-kpi2--${this.kpiTone}` : null;
        },
        // Trailing status icon (builder-v1 A+ card only): the user's picked icon, else a default
        // alert glyph when the tone is critical and no icon was chosen (the showcase warn mark).
        // Rendered AFTER the value + unit. null when there is nothing to show.
        kpiTrailingIcon() {
            if (this.kpiIcon) return this.kpiIcon;
            return this.kpiTone === 'critical' ? 'mdi-alert' : null;
        },
        // A+ KPI card gate: a count widget renders the full ratified .kpi2 card anatomy (its
        // own labeled header + hairline meta) ONLY in the custom-dashboard builder context
        // (builderHeader, passed solely by DashboardItem / the wizard preview). Used to
        // suppress the generic title row so the kpi2 header isn't duplicated. Flag-off /
        // chatbot / EditAIWidget (no builderHeader) → false → legacy count box + generic
        // title row render byte-identical.
        listDrillTooltip() {
            return LIST_DRILL_TOOLTIP;
        },
        /** Screen-reader name of the drill button: the action plus what the number is. */
        listDrillAriaLabel() {
            const title = this.message && this.message.title;
            return title ? `${LIST_DRILL_TOOLTIP}: ${title}` : LIST_DRILL_TOOLTIP;
        },
        isBuilderKpi() {
            return !!this.builderHeader && !!this.message && this.message.messageType === 'count';
        },
        // The legacy KPI branch still reads this computed. Use the shared reader formatting so
        // both card variants retain staging's two-decimal display without inventing null values.
        displayCount() {
            return metricIsKnown(this.count) ? formatDashboardNumber(this.count) : 'Not available';
        },
        // A+ KPI meta line (builder count card): "<source> · <Function> of <column>", e.g.
        // "Anomaly Register · Count of RowIndex". Built only from the saved sqlViewConfig
        // (tableDisplayName + first metric) — the same shape SqlViewAggregationBuilder's
        // defaultAlias produces — so it reads as an honest source/aggregation caption rather
        // than the broken "count (0)" subtitle. Degrades gracefully: source-only when there is
        // no metric, '' when there is no config.
        kpiMetaText() {
            const cfg = this.parsedSqlViewConfig;
            if (!cfg) return '';
            const parts = [];
            const source = cfg.tableDisplayName || cfg.tableName;
            if (source) parts.push(source);
            const agg = cfg.aggregation || {};
            const metric = Array.isArray(agg.metrics) ? agg.metrics[0] : null;
            if (metricFn(metric)) {
                parts.push(metricLabel(metric, cfg.columnDisplayNames || {}));
            }
            return parts.join(' · ');
        },
        // The KPI footer string (builder count card): the authored footnote
        // (sqlViewConfig.footnote) when non-empty, otherwise the auto source/aggregation meta.
        kpiFooterText() {
            const cfg = this.parsedSqlViewConfig;
            const footnote = cfg && typeof cfg.footnote === 'string' ? cfg.footnote.trim() : '';
            return plainDashboardCopy(footnote) || this.kpiMetaText;
        },
        // Split the footer into ordered segments of digit-runs vs surrounding text so the
        // template can render each as a <span> and bold the numbers (weight 600, ink) WITHOUT
        // v-html, reproducing the showcase .kpi2__meta b look for any authored copy. A digit run
        // is an integer with optional decimal / thousands groups (0.6, 1,234); trailing
        // punctuation stays with the text run.
        kpiFooterSegments() {
            const text = this.kpiFooterText;
            if (!text) return [];
            const segments = [];
            const re = /\d+(?:[.,]\d+)*/g;
            let last = 0;
            let m;
            while ((m = re.exec(text)) !== null) {
                if (m.index > last) segments.push({ text: text.slice(last, m.index), bold: false });
                segments.push({ text: m[0], bold: true });
                last = m.index + m[0].length;
            }
            if (last < text.length) segments.push({ text: text.slice(last), bold: false });
            return segments;
        },
        // True only when every metric's aggregation is additive (COUNT/SUM), so the
        // grand total (subtitle "(N)" + donut center number) is arithmetically valid.
        // AVG/MIN/MAX → false (summing per-group results is meaningless); empty/missing
        // metrics → false. Mirrors widgetSubtitle's fn extraction for legacy shapes.
        isAdditiveAggregation() {
            const cfg = this.parsedSqlViewConfig;
            const metrics = cfg && Array.isArray(cfg.aggregation?.metrics) ? cfg.aggregation.metrics : [];
            if (!metrics.length) return false;
            return metrics.every(m => {
                const fn = metricFn(m);
                return typeof fn === 'string' && ADDITIVE_AGGREGATIONS.includes(fn.toUpperCase());
            });
        },
        // Pure ECharts option for the echarts branch (D5): reads message.content via
        // prepareChartData (single-series) or prepareMatrixData (matrix types), builds a fresh
        // option, mutates nothing on message.
        echartsOption() {
            if (this.chartEngine !== 'echarts') return null;
            // Production multi-select (W2, decision 8): enabled for ELIGIBLE types only —
            // single-group-by category charts (bar/column/line/area/pie/donut). Matrix /
            // multi-series types (isMatrixType) are excluded entirely: no selectedMode, no
            // emit, so the old groupBy[0] single-click cross-filter hole is closed. Also
            // gated on a cross-filter CONSUMER being present ($listeners.legendFilter): the
            // selection's source of truth is the downward prop, so an eligible chart with no
            // dashboard round-trip (wizard preview, chatbot, EditAIWidget) would select then
            // revert on every click — the guard keeps those contexts non-interactive instead.
            const cfg = this.parsedSqlViewConfig || {};
            const hasSyntheticDifferenceBuckets = !!cfg.aggregation?.differenceBucketGroup;
            const multiSelect = !this.isMatrixType
                && !hasSyntheticDifferenceBuckets
                && !!(this.$listeners && this.$listeners.legendFilter);
            // Multi-series (matrix) types shape their own categories × series from the raw dict rows
            // and route the result through the SAME buildChartOption switch. A config/key error or an
            // empty matrix yields no option — the template's matrix states handle those. The matrix
            // builder ignores the coloring adapter (matrix schema coloring is out of scope), so its
            // ctx stays minimal — no groupByColumn/centerLabel/coloring keys.
            if (this.isMatrixType) {
                const matrix = this.matrixData;
                if (!matrix || matrix.error || !matrix.series.length) return null;
                return buildChartOption(this.message.messageType, matrix, {
                    // Size -> legacy 0/1/2 so buildChartOption's resolveFontSizePx yields identical px
                    // for every legacy input (incl. absent: normal -> 1 -> 16 == the pipeline default).
                    fontSize: LEGACY_FONT_SIZE_BY_WIDGET_SIZE[this.resolvedWidgetSize],
                    legend: this.message.legend,
                    mini: !!this.mini,
                    multiSelect,
                    showcase: !!this.builderHeader,
                    stackMode: cfg.stackMode,
                    axisByMetric: cfg.axisByMetric,
                    // Dual-axis per-metric line color + type (alias-keyed); ignored by the other
                    // matrix builders. Absent/{} → every series keeps its palette color + solid line.
                    seriesStyles: cfg.seriesStyles,
                    seriesLabels: this.matrixSeriesLabels,
                    referenceMetricAlias: cfg.comparison && cfg.comparison.referenceMetricAlias,
                    // Heatmap: the cell-value tooltip label + the theme-aware visualMap ramp. Ignored
                    // by the other matrix builders.
                    valueLabel: this.matrixValueLabel,
                    theme: (this.$vuetify && this.$vuetify.theme && this.$vuetify.theme.dark) ? THEME.DARK : THEME.LIGHT,
                });
            }
            const prepared = this.prepareSingleSeriesData();
            // Reference loading gate (charts): a 'reference' selection whose tab library is still
            // loading (librarySchemas null) must NOT activate the schema path — suppress coloringSource
            // so the adapter keeps the default palette (no schemaUnavailable warning) until the library
            // lands, mirroring the KPI gate. builtin / inline / non-schema widgets are unaffected.
            const referenceLoading = !!(cfg.schemaSelection
                && cfg.schemaSelection.kind === SCHEMA_KIND.REFERENCE
                && this.librarySchemas == null);
            const chartColoringSource = referenceLoading ? undefined : cfg.coloringSource;
            const option = buildChartOption(this.message.messageType, prepared, {
                // Size -> legacy 0/1/2 so buildChartOption's resolveFontSizePx yields identical px
                // for every legacy input (incl. absent: normal -> 1 -> 16 == the pipeline default).
                fontSize: LEGACY_FONT_SIZE_BY_WIDGET_SIZE[this.resolvedWidgetSize],
                legend: this.message.legend,
                mini: !!this.mini,
                multiSelect,
                // Showcase chart treatment (severity colormap, centered ring, bottom
                // legend, donut center total) — gated on the custom-dashboard context
                // (builderHeader is only passed by DashboardItem, and only when the
                // builder-v1 flag is on). Flag-off / other consumers → false → the
                // chart option is byte-identical to today.
                showcase: !!this.builderHeader,
                // GroupBy column name (schema) so the pie's RAG-vs-severity decision is
                // stable under filtering — see echartsOptions.isSeverityColumn.
                groupByColumn: this.groupByColumn,
                groupByDataType: this.groupByDataType,
                // Per-widget donut center noun (delta 1); null → builder keeps its
                // neutral 'total'. Only read in the showcase donut center graphic.
                centerLabel: this.centerLabel,
                // Additivity gate: the donut center total is drawn only for COUNT/SUM
                // widgets; AVG/MIN/MAX leave a clean hole instead of fake math.
                additiveTotal: this.isAdditiveAggregation,
                // Coloring-schema chart hop (§4.7): hand the adapter the raw source + selection from
                // the widget's sqlViewConfig (the same keys the KPI branch reads) plus the tab's
                // schema library for reference resolution. The adapter's null path (coloringSource
                // !== 'schema') returns the existing colors verbatim, so a legacy chart — schema keys
                // undefined here — is byte-identical. `theme` is read CONDITIONALLY off the (gated)
                // source: only a schema chart touches $vuetify.theme.dark, so only schema charts
                // register a theme dependency and re-render on a light/dark flip; a legacy chart adds
                // no new reactive dep (Vue 2 collects dependencies per getter run).
                coloringSource: chartColoringSource,
                schemaSelection: referenceLoading ? undefined : cfg.schemaSelection,
                // librarySchemas resolves 'reference' selections (registry); ignored for
                // builtin / inline. Content-keyed in the engine, so no version is passed.
                librarySchemas: this.librarySchemas,
                theme: chartColoringSource === COLORING_SOURCE.SCHEMA
                    ? ((this.$vuetify && this.$vuetify.theme && this.$vuetify.theme.dark) ? THEME.DARK : THEME.LIGHT)
                    : undefined,
            });
            // Dashboard multi-select for pie/donut: the legend must not be a second,
            // conflicting interaction surface. A legend click otherwise toggles slice
            // VISIBILITY (ECharts default) while the board does NOT cross-filter, which reads
            // as a broken filter. Disable legend selection so the slice (datum) is the sole
            // selection surface. Gated on our multiSelect flag (dashboard context with a
            // cross-filter consumer); chatbot/preview keep standard ECharts legend toggling.
            if (option && multiSelect
                && (this.message.messageType === 'pie' || this.message.messageType === 'donut')
                && option.legend) {
                const legends = Array.isArray(option.legend) ? option.legend : [option.legend];
                legends.forEach(l => { if (l) l.selectedMode = false; });
            }
            // Difference-bucket labels are calculated result bands, not reversible source values.
            // Disable native datum selection as well as dashboard multi-select so clicking a slice
            // cannot present a highlight that has no truthful cross-filter behind it.
            if (option && hasSyntheticDifferenceBuckets && Array.isArray(option.series)) {
                option.series.forEach(series => { series.selectedMode = false; });
            }
            return option;
        },
        // ── Multi-series ("matrix") types: stacked column, … ───────────────────────────
        // True for the types that consume prepareMatrixData (categories × series) instead of the
        // single-series prepareChartData path.
        isMatrixType() {
            return MATRIX_TYPES.includes(this.message && this.message.messageType);
        },
        // Categories × series matrix for a matrix widget, or null for every other type. Pure read of
        // message.content through prepareMatrixData with the widget's aggregation + baked TopBottom
        // (the truncation sentinel's cap). Logging lives in the matrixData watcher (kept pure here).
        matrixData() {
            if (!this.isMatrixType) return null;
            const cfg = this.parsedSqlViewConfig || {};
            const config = { aggregation: cfg.aggregation, topBottom: cfg.topBottom };
            const content = this.message && this.message.content;
            // Route to the matching normalizer: dual-axis line's series are the metrics
            // (prepareMetricSeriesData); stacked column's series come from groupBy[1] (prepareMatrixData).
            if (this.message && this.message.messageType === DUAL_AXIS_LINE) {
                return prepareMetricSeriesData(content, config);
            }
            if (this.message && this.message.messageType === MIXED_COMPARISON) {
                return prepareComparisonData(content, { ...config, comparison: cfg.comparison });
            }
            return prepareMatrixData(content, config);
        },
        // Friendly display labels for matrix series keyed by the generated metric alias, e.g.
        // { avg_corrosionrate: 'Average of Corrosion Rate' } — mirrors kpiMetaText's label logic
        // (function word + column display name). Consumed by the dual-axis builder for its legend +
        // tooltip so they read as labels, not alias slugs. Stacked column ignores it (its series are
        // real groupBy[1] data values). {} when not a matrix widget.
        matrixSeriesLabels() {
            if (!this.isMatrixType) return {};
            const cfg = this.parsedSqlViewConfig || {};
            const metrics = cfg.aggregation && Array.isArray(cfg.aggregation.metrics) ? cfg.aggregation.metrics : [];
            const displayNames = cfg.columnDisplayNames || {};
            const map = {};
            metrics.forEach(m => {
                if (!m || !m.alias) return;
                map[m.alias] = metricLabel(m, displayNames) || m.alias;
            });
            return map;
        },
        // The first metric's friendly label — the heatmap cell-value label for its tooltip. '' when
        // absent; the builder falls back to 'value'.
        matrixValueLabel() {
            if (!this.isMatrixType) return '';
            const cfg = this.parsedSqlViewConfig || {};
            const metrics = cfg.aggregation && Array.isArray(cfg.aggregation.metrics) ? cfg.aggregation.metrics : [];
            const alias = metrics[0] && metrics[0].alias;
            return (alias && this.matrixSeriesLabels[alias]) || '';
        },
        // An ECharts-only type (no Apex builder) whose engine resolved to Apex (chunk-load failure):
        // the widget shows an explicit "engine required" state instead of Apex's silent blank chrome.
        echartsOnlyUnavailable() {
            return this.chartEngine === 'apex'
                && ECHARTS_ONLY_TYPES.includes(this.message && this.message.messageType);
        },
        // The typed error code when a matrix widget's saved aggregation can't produce an unambiguous
        // shape (Codex R1-2), else null → the widget renders a config-error state, never a wrong chart.
        matrixConfigError() {
            const matrix = this.matrixData;
            return matrix && matrix.error ? matrix.error : null;
        },
        // Friendly copy for the config-error state, mapped from the typed error code.
        matrixErrorMessage() {
            switch (this.matrixConfigError) {
                case MATRIX_ERROR.KEY_NOT_IN_DATA:
                case COMPARISON_ERROR.KEY_NOT_IN_DATA:
                case COMPARISON_ERROR.REFERENCE_ALIAS_NOT_FOUND:
                    return 'This chart’s data no longer matches its setup. Edit the widget and save it again.';
                case COMPARISON_ERROR.MISSING_GROUP_BY:
                case COMPARISON_ERROR.MISSING_METRIC_ALIASES:
                case COMPARISON_ERROR.DUPLICATE_METRIC_ALIASES:
                    return 'This comparison needs at least one group and two unique calculations. Edit the widget to set them.';
                case MATRIX_ERROR.MISSING_KEYS:
                case MATRIX_ERROR.DUPLICATE_KEYS:
                default:
                    return 'This chart needs a category, a series to split by, and a value. Edit the widget to set them.';
            }
        },
        // The matrix hit the baked row cap (Codex R1-5): surface an unobtrusive "data truncated"
        // notice so low-value cells aren't silently indistinguishable from no-data cells.
        matrixTruncated() {
            const matrix = this.matrixData;
            return !!(matrix && matrix.possiblyTruncated);
        },
        // ── Gauge (count-path display variant, W-A) ────────────────────────────────────
        // A count widget whose sqlViewConfig.kpiVariant is 'gauge' renders a dial (via ECharts on the
        // scalar path) instead of the KPI number card. Gated on !echartsUnavailable so a chunk-load
        // failure falls back to the plain count card. A plain count widget (no kpiVariant) → false →
        // the existing count branches render byte-identical, including schema-colored KPIs.
        isGaugeKpi() {
            const cfg = this.parsedSqlViewConfig;
            return !!cfg && !!this.message && this.message.messageType === 'count'
                && cfg.kpiVariant === 'gauge' && !this.echartsUnavailable;
        },
        // Pure gauge option for the gauge branch: reads this.count (the SAME scalar the KPI card
        // shows, so a re-query repaints the dial) + the widget's static gauge config, theme-aware.
        gaugeEchartsOption() {
            if (!this.isGaugeKpi) return null;
            const cfg = this.parsedSqlViewConfig || {};
            const theme = (this.$vuetify && this.$vuetify.theme && this.$vuetify.theme.dark) ? 'dark' : 'light';
            // Metric label ("Average of Remaining Life") shown under the dial value — mirrors
            // kpiMetaText's function-word + column-display-name logic.
            const metric = cfg.aggregation && Array.isArray(cfg.aggregation.metrics) ? cfg.aggregation.metrics[0] : null;
            // Gauge keeps the sub-dial caption blank for a column-less metric (a bare count),
            // so guard on the column before formatting rather than emitting "Count of records".
            const col = metric && (metric.column || metric.name || metric.field);
            const label = col ? metricLabel(metric, cfg.columnDisplayNames || {}) : '';
            return buildGaugeOption(this.count, cfg.gauge, { theme, unit: cfg.unit, label, scale: this.gaugeSizeScale });
        },
        canIsolateDetailsRow() {
            if (!this.rowDetailsRow || this.message?.messageType !== 'datagrid') return false;
            return !!this.$refs.sqlViewDataGrid?.canIsolateRow?.(this.rowDetailsRow);
        },
        hasExternalRowDetailsConsumer() {
            return !!(this.$listeners && this.$listeners.openRowDetails);
        },
    },
    mounted() {

        if (this.$refs.messageWrapper) {

            this.resizeObserver = new ResizeObserver(entries => {
                for (let entry of entries) {
                    this.setChartWrapperSize();

                    if (!this.message)
                        return;

                    setTimeout(() => {
                        // ECharts self-resizes via its own ResizeObserver (D6) — the
                        // :key remount hack is Apex-only. Checked at callback time.
                        if (this.chartEngine === 'echarts') return;
                        this.key = Date.now();
                    },);
                }
            });
            this.resizeObserver.observe(this.$refs.messageWrapper);
        }
        this.setChartWrapperSize();
        if (!this.message) return;
         {
             this.buildMessage(this.message);
         }

    },
    destroyed(){
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    },
    methods: {
        /** Attributes of the KPI value row: button semantics only while it drills. */
        listDrillAttrs(tooltipAttrs) {
            if (!this.listDrillable) return {};
            return {
                ...tooltipAttrs,
                type: 'button',
                'aria-label': this.listDrillAriaLabel,
                'aria-busy': this.listDrillBusy ? 'true' : 'false',
            };
        },
        /** Listeners of the KPI value row: the tooltip's plus the drill click, only while it drills. */
        listDrillListeners(tooltipOn) {
            if (!this.listDrillable) return {};
            return { ...tooltipOn, click: this.emitListDrill };
        },
        emitListDrill() {
            if (this.listDrillBusy) return;
            // The shown value rides along so the parent can cross-check it against the list size.
            this.$emit('listDrill', { value: this.count });
        },
        prepareSingleSeriesData() {
            let prepared = prepareChartData(this.message?.content || [], this.message?.messageType);
            if (this.message?.messageType === 'line' || this.message?.messageType === 'area') {
                prepared = sortPreparedDateSeries(prepared, this.groupByDataType);
            }
            return prepared;
        },
        openRowDetails(row) {
            if (!row || typeof row !== 'object') return;
            if (this.hasExternalRowDetailsConsumer) {
                const grid = this.$refs.sqlViewDataGrid;
                this.$emit('openRowDetails', {
                    row,
                    config: this.parsedSqlViewConfig,
                    canIsolate: !!grid?.canIsolateRow?.(row),
                });
                return;
            }
            this.rowDetailsRow = row;
            this.rowDetailsOpen = true;
        },
        selectTableRow(item) {
            if (!item || typeof item !== 'object') return;
            this.selectedTableRow = item;
            this.$nextTick(() => this.$refs.table?.$el?.focus?.());
        },
        openTableRowDetails(event, data) {
            const row = data?.item || data;
            if (!row || typeof row !== 'object') return;
            this.selectedTableRow = row;
            this.openRowDetails(row);
        },
        openSelectedTableRow() {
            if (this.selectedTableRow) this.openRowDetails(this.selectedTableRow);
        },
        currentTablePageItems() {
            const visibleItems = this.$refs.table?.internalCurrentItems;
            return Array.isArray(visibleItems) ? visibleItems : this.items.slice(0, 5);
        },
        ensureTableSelection() {
            const visibleItems = this.currentTablePageItems();
            if (!visibleItems.includes(this.selectedTableRow)) this.selectedTableRow = visibleItems[0] || null;
        },
        moveTableSelection(direction) {
            const visibleItems = this.currentTablePageItems();
            if (!visibleItems.length) return;
            const current = visibleItems.indexOf(this.selectedTableRow);
            const next = current < 0
                ? (direction > 0 ? 0 : visibleItems.length - 1)
                : Math.max(0, Math.min(visibleItems.length - 1, current + direction));
            this.selectedTableRow = visibleItems[next];
        },
        tableRowClass(item) {
            return item === this.selectedTableRow
                ? 'cdb-table-row--interactive cdb-table-row--selected'
                : 'cdb-table-row--interactive';
        },
        isolateDetailsRow(row) {
            const grid = this.$refs.sqlViewDataGrid;
            if (!grid?.canIsolateRow?.(row)) return;
            this.rowDetailsOpen = false;
            this.$nextTick(() => grid.onViewerClick(row));
        },
        getGridExplanationScope() {
            return this.$refs.sqlViewDataGrid?.getExplanationScope?.() || null;
        },
        prepareIsolationOwnershipChange() {
            this.$refs.sqlViewDataGrid?.prepareIsolationOwnershipChange?.();
        },
        cancelPendingIsolation(operation = null) {
            this.$refs.sqlViewDataGrid?.cancelPendingIsolation?.(operation);
        },
        setChartWrapperSize() {
            var el = this.$refs.messageWrapper;
            if (!el) return;
            var width = el.clientWidth;

            var height = el.clientHeight- this.$refs.titleWrapper?.clientHeight;

             if (this.$refs.chartWrapper) {
                var isCircular = this.message && (this.message.messageType === 'pie' || this.message.messageType === 'donut');
                var skipSquare = this.builderHeader && this.chartEngine === 'echarts';
                if (isCircular && !skipSquare) {
                    // Legacy Apex circular charts distort unless their wrapper is square, so
                    // the square is kept for ANY Apex circular chart (engine-gated), not just
                    // non-showcase ones. Only a showcase (custom-dashboard v1) donut rendered
                    // via ECharts skips it: ECharts keeps the ring circular via its own radius %
                    // + center, so it takes the full-width else path to fill the card
                    // (matches dashboard-manager.html). Apex-anywhere and non-showcase keep the
                    // square, so a showcase widget that ever fell back to Apex won't distort.
                    var side = Math.min(height, width);
                    this.$refs.chartWrapper.style.width = `${side}px`;
                    this.$refs.chartWrapper.style.height = `${side}px`;
                } else {
                    // All other charts use the full available space
                    this.$refs.chartWrapper.style.width = `${width}px`;
                    this.$refs.chartWrapper.style.height = `${height}px`;
                }
            }
            // Structured (dashboard-grid) datagrids now fill the card via flex
            // (.message-wrapper--dashboard-grid column -> .table-wrapper--dashboard-grid flex:1),
            // so they take no JS height write here — an inline px height would fight the flex-fill
            // and bleed past the card. The compact/legacy table consumers still size from measured
            // height. Chart sizing above is untouched.
            if (this.$refs.tableWrapper && !this.dashboardGridStyle) {
               this.$refs.tableWrapper.style.height = `${height-20}px`;
            }
        },
        refresh(){

            this.key=Date.now();
            this.chartReady=false;
            setTimeout(()=>{

                this.buildMessage(this.message);
            })

        },

        buildMessage(data) {
            // Engine-aware (D11): echarts widgets never run the Apex prep
            // (buildChart/setChartData) and never toggle chartReady on re-query —
            // the echartsOption computed reacts to the new message and pushes an
            // idempotent setOption (no remount). On first load the async renderer
            // reveals the widget; until then chartReady stays false so the existing
            // loader shows (never a blank chart-wrapper). datagrid/table/count/apex
            // paths below are untouched.
            if (this.chartEngine === 'echarts') {
                if (!this.chartReady) this.revealEchartsWhenReady();
                return;
            }
            this.chartReady=false;
            if (this.message.messageType == 'datagrid') {
                // datagrid manages its own data via SqlViewDataGrid
            }
            else if (this.message.messageType == 'table') {
                this.buildTable(data);

            }
            else if (this.message.messageType == 'count') {
                this.buildCountmessage(data);
            }
            else if (ECHARTS_ONLY_TYPES.includes(this.message.messageType)) {
                // The engine fell back to Apex for a type that has NO Apex builder (matrix types
                // bypass the backend isChart flatten). Skip the broken Apex prep entirely; the
                // template's echartsOnlyUnavailable branch shows an explicit "engine required" state.
                log('dashboard-widget', 'echarts-only type on apex fallback — engine-required notice', {
                    messageType: this.message.messageType, messageId: this.message?.id,
                });
            }
            else {
                this.buildChart(data);
            }
            setTimeout(()=>{
                // Stale-timer guard (I-4): if the engine flipped to echarts after this
                // Apex readiness timer was scheduled, hand readiness to the reveal path
                // rather than forcing chartReady=true (which would blank the wrapper
                // while the echarts chunk is still loading).
                if (this.chartEngine === 'echarts') return;
                this.chartReady=true;
                setTimeout(()=>{
                    if (this.chartEngine === 'echarts') return;
                    this.setChartWrapperSize();
                    this.key=Date.now();
                })
            })
        },
        guidToHexColor(guid) {
            if(!guid){
                guid=$uuidv4()
            }
            let hash = 0;
            for (let i = 0; i < guid.length; i++) {
                hash = guid.charCodeAt(i) + ((hash << 5) - hash);
            }
            let color = (hash & 0xFFFFFF).toString(16).toUpperCase();
            return `#${'000000'.substring(0, 6 - color.length)}${color}`;
        },
        buildCountmessage(data) {
            if (!this.hasData) {
                return;
            }
            this.title = Object.keys(this.message.content[0])[0];
            this.count = this.extractCountValue(this.message.content[0]);
        },
        // A KPI/count widget must render the metric total, not the group label.
        // Grouped/normalized content carries the metric under `value`/`Value`; raw
        // aggregate rows are SELECT [groupBy…], [metric…] so the metric is the LAST
        // column. Pick that rather than the first column (the group label for grouped data).
        metricIsKnown,
        formatDashboardNumber,
        extractCountValue(row) {
            if (row == null || typeof row !== 'object') return row;
            if (row.value !== undefined && row.value !== null) return row.value;
            if (row.Value !== undefined && row.Value !== null) return row.Value;
            var values = Object.values(row);
            for (var i = values.length - 1; i >= 0; i--) {
                if (typeof values[i] === 'number') return values[i];
            }
            return values.length ? values[values.length - 1] : 0;
        },
        buildTable(data) {
            if(!this.hasData){
                return;
            }
            this.headers = Object.keys(this.message.content[0]).map(e => ({ text: e, value: e }));
            this.items = this.message.content;
        },
        buildChart() {

            this.updateChartOptions();
            this.setChartData();

            this.options.chart.toolbar = {
                show: false, // Hides the toolbar entirely
            }
            this.options.title = { text: "" };
            //this.options.chart.height='100%';
            // this.options.legend={
            //     show:false
            // }

            if(this.message.legend){
                this.options.legend = {
                    ...this.options.legend,
                    ...this.message.legend,
                    onItemClick: { toggleDataSeries: true },
                };
            }
        },
        setChartData(){

                if(!this.hasData)
                return;

                var prepared = this.prepareSingleSeriesData();
                var labels = prepared.labels;
                var series = prepared.series;
                var colors = prepared.colors;
                this.preparedLabels = labels;

                //set data
                if(colors[0]) //if colors have value
                        this.options.fill={
                            colors
                        }
                //

                switch (this.message.messageType) {
                    case 'bar':
                        this.options.xaxis.categories=labels;
                        this.message.series=[{data:series}];

                        break;
                        case 'column':
                        this.options.xaxis.categories=labels;
                        this.message.series=[{data:series}];

                        break;
                    case 'pie':
                        this.options.labels=labels;
                        this.message.series=series;
                        break;
                        case 'donut':
                        this.options.labels=labels;
                        this.message.series=series;
                        break;
                        case 'area':
                        this.options.labels=labels;
                        this.message.series=[{data:series}];
                        break;
                        case 'line':
                        this.options.labels=labels;
                        this.message.series=[{data:series,type:'line'}];
                        break;
                    default:
                        break;
                }
        },
        // Resolve this widget's cross-filter target from its saved config — the SAME
        // resolution the single-value path used (R4-2/R4-5): deterministic groupBy[0],
        // else the legacy key fallback ('label'/'Label', else keys[0]) for pre-groupBy
        // widgets. Returns { tableName, filterColumn, tagColumn } or null when the widget
        // cannot cross-filter (missing table/column).
        resolveSelectionTarget() {
            const config = this.parsedSqlViewConfig;
            if (config?.aggregation?.differenceBucketGroup) {
                log('dashboard-widget', 'selection: synthetic difference bucket has no source-column target', {
                    messageId: this.message?.id,
                });
                return null;
            }
            const tableName = config?.tableName || this.message?.tableName || null;
            const availableColumns = Array.isArray(config?.columns) && config.columns.length
                ? config.columns
                : (this.message?.content?.length ? Object.keys(this.message.content[0]) : []);
            const serverTagLink = config && Object.prototype.hasOwnProperty.call(config, 'tagLink')
                ? config.tagLink
                : undefined;
            const tagLink = resolveTagLink({
                serverTagLink,
                legacyTagColumn: config?.tagColumn,
                tableName,
                availableColumns,
            });
            let filterColumn = config?.aggregation?.groupBy?.[0] || null;
            if (!filterColumn && this.message?.content?.length) {
                const keys = Object.keys(this.message.content[0]);
                filterColumn = keys.find(k => k === 'label' || k === 'Label') || keys[0];
                log('dashboard-widget', 'selection: legacy filterColumn fallback', {
                    filterColumn, messageId: this.message?.id,
                });
            }
            if (!filterColumn || !tableName || !tagLink) {
                log('dashboard-widget', 'selection: missing filter column, table, or tag link', {
                    filterColumn, tableName, tagLink, config: this.parsedSqlViewConfig, messageId: this.message?.id,
                });
                return null;
            }
            return { tableName, filterColumn, tagColumn: tagLink.column, tagLink };
        },
        // The ONE cross-filter emit builder for BOTH engines. `labels` is this widget's
        // ENTIRE current selection as display labels (ECharts: the full selectchanged set;
        // Apex: a single value toggled against the prop). Emits the pinned multi-value
        // payload under the existing `legendFilter` event; `values: []` means "clear my
        // selection". Validation runs BEFORE emit (R4-2) — a widget that can't cross-filter
        // emits nothing and its native highlight is reverted by the caller's reconcile.
        // Per value: display carries the human label ('Unset'); value is the server value,
        // null for the Unset bucket (R4-4).
        emitSelection(labels) {
            // Point #8 item 3: new chart selections are disabled while a refresh settles (the scrim
            // also blocks pointer input, but a programmatic selectchanged from the post-fetch
            // setOption could still reach here). Never mutate the cross-filter mid-request.
            if (this.refreshing) {
                log('dashboard-widget', 'selection ignored during refresh', { messageId: this.message?.id });
                return;
            }
            const target = this.resolveSelectionTarget();
            if (!target) {
                // Visible signal (reuse the app's notify convention) — never a silent drop.
                this.$dialog?.notify?.warning('This widget is not set up for cross-filtering.', {
                    position: 'top-right',
                    timeout: 3000,
                });
                return;
            }
            const cfg = this.parsedSqlViewConfig;
            // The chart fill colours an un-schemed category by its RANK in prepareChartData().labels
            // (chart-data.service). Ship that EXACT label domain so a chart-selection 3D isolate ranks
            // the same value over the same set → identical hue (isolateColor 'auto' + CustomDashboard).
            const preparedForDomain = this.prepareSingleSeriesData();
            const labelDomain = (preparedForDomain && Array.isArray(preparedForDomain.labels))
                ? preparedForDomain.labels
                : [];
            // The aggregated number each category shows ON THE CHART (prepareChartData().series,
            // index-aligned with labels). The 3D legend reports this as the selection's real data
            // count beside the painted-geometry count, so the two can never silently disagree.
            // Shared with the widget-header isolate path so both legends quote the same numbers.
            const datumValues = preparedDatumValues(preparedForDomain);
            const values = (labels || []).map(l => ({ value: l === 'Unset' ? null : l, display: l }));
            log('dashboard-widget', 'emitSelection', { count: values.length, messageId: this.message?.id });
            this.$emit('legendFilter', {
                widgetId: this.message.id,
                sourceKey: this.sourceKey,
                tableName: target.tableName,
                filterColumn: target.filterColumn,
                tagColumn: target.tagColumn,
                tagLink: target.tagLink,
                // Saved + runtime filter arrays exactly as the render path applies them (saved
                // sqlViewConfig.filters server-side, runtime widgetFilters riding the query), so
                // each dimension resolves under ITS chart's full query scope on the server. Same
                // {column, operator, value} schema on both sides; the server ANDs the list.
                filters: [
                    ...((cfg && cfg.filters) || []),
                    ...((this.widgetFilters && Array.isArray(this.widgetFilters.filters)) ? this.widgetFilters.filters : []),
                ],
                // 3D-isolation colouring so a chart-selection isolate paints its elements the same
                // way the datagrid "Isolate All" does. `coloring` = legacy static { column, valueColors }
                // (Link-3D wizard step); `coloringSource`/`schemaSelection` = the chart FILL's live
                // registry-schema colouring. When the fill is schema-driven the isolate resolves each
                // element through the SAME live schema (so schema edits recolour the isolate too);
                // otherwise it falls back to the static map. All null -> plain (uncoloured) isolate.
                coloring: (cfg && cfg.coloring) || null,
                coloringSource: (cfg && cfg.coloringSource) || null,
                schemaSelection: (cfg && cfg.schemaSelection) || null,
                renderedValueColors: this.renderedChartCategoryColors(),
                // Which of the two colour definitions above the widget declared as the winner. Both
                // can be set at once and they routinely disagree, so without this the isolate had to
                // guess (it always preferred the schema, invisibly). Absent → that legacy order.
                highlightColorSource: (cfg && cfg.highlightColorSource) || null,
                // This chart's own point-of-interest choice. Absent → the register's declared
                // default, which is how every chart behaved before charts had a per-widget picker.
                poiSource: (cfg && cfg.poiSource) || null,
                // The chart's own category domain — the isolate ranks an 'auto' (un-schemed) colour
                // over THIS set so its hue matches the chart fill exactly (guaranteed, no re-fetch).
                labelDomain,
                datumValues,
                // How this widget NAMES its group-by column, and its own heading. The 3D legend is a
                // caption for this chart, so it must read the way the chart does rather than echoing
                // the raw SQL identifier.
                filterColumnLabel: columnDisplayName(target.filterColumn, cfg && cfg.columnDisplayNames),
                widgetTitle: (this.message && this.message.title) || null,
                values,
            });
        },
        renderedChartCategoryColors() {
            return extractRenderedCategoryColors(this.echartsOption);
        },
        // Apex (degraded/fallback engine) legend/datapoint click — SINGLE-value toggle in
        // the multi-value contract: re-clicking the selected value clears it, any other
        // value replaces the selection. Eligible ECharts types accumulate natively; Apex
        // stays single-value (documented fallback). Routes through the shared builder.
        onLegendClick(label) {
            if (label == null) return;
            const current = this.chartSelectionLabels;
            const next = current.includes(label) ? [] : [label];
            this.emitSelection(next);
        },
        updateChartOptions() {

            var options={};
            var fontOptions = {

                xaxis: {
                    categories: [],
                    labels: {
                        style: {
                            fontSize: `${this.fontSize}px`
                        }
                    },
                }
                ,
                yaxis: {
                    labels: {
                        style: {
                            fontSize: `${this.fontSize}px`
                        }
                    }
                },
                dataLabels: {
                    enabled: true,
                    style: {
                        fontSize: `${this.fontSize}px`
                    }
                }
            };
            Object.assign(options, fontOptions);
            switch (this.message.messageType) {
                case 'bar':
                    Object.assign(options, {
                        chart: {
                            type: 'bar',
                        },
                        plotOptions: {
                            bar: {
                                horizontal: true,
                                distributed: true,
                                borderRadius: 5,
                                borderRadiusApplication: 'end'
                            },
                        },
                    });

                    break;
                case 'pie':
                    Object.assign(options, {
                        chart: {
                            type: 'pie',
                        },

                    });
                    break;
                case 'donut':
                    Object.assign(options, {
                        chart: {
                            type: 'pie',
                        }

                    });
                    break;
                case 'column':
                    Object.assign(options, {
                        chart: {
                            type: 'bar',
                        },
                        plotOptions: {
                            bar: {
                                horizontal: false,
                                distributed: true,
                            },
                        }, dataLabels: {
                            enabled: false
                        }
                    });
                    break;
                case 'area':
                    Object.assign(options, {
                        chart: {
                            type: 'area',
                        },

                        stroke: {
                            curve: 'straight'
                        }
                    });
                    options.xaxis.labels = {
                        rotate: -90,
                        rotateAlways: true
                    };
                    options.yaxis.title = {
                        text: ''
                    };
                    break;
                case 'line':
                    Object.assign(options, {
                        chart: {
                            type: 'line',
                        },

                        stroke: {
                            curve: 'straight'
                        }
                    });
                    options.xaxis.labels = {
                        rotate: -90,
                        rotateAlways: true
                    };
                    options.yaxis.title = {
                        text: ''
                    };
                    break;
            }

            // Force ApexCharts to fill its container instead of auto-sizing
            if (!options.chart) options.chart = {};
            options.chart.height = '100%';
            options.chart.width = '100%';

            // Inject chart click events for cross-filtering. Apex keeps its index
            // path — resolve the index to a label via preparedLabels, then hand the
            // label to the shared label-based core (both engines converge on it).
            const self = this;
            options.chart.events = {
                ...(options.chart.events || {}),
                legendClick(chartContext, seriesIndex) {
                    // D12: only categorical legends filter. bar/column are distributed
                    // (legend entry index === category index) and pie/donut legends are
                    // slices; line/area legends name the single series → must NOT filter
                    // (else seriesIndex 0 → preparedLabels[0] = first category, I-3).
                    const t = self.message?.messageType;
                    if (t === 'bar' || t === 'column' || t === 'pie' || t === 'donut') {
                        self.onLegendClick(self.preparedLabels[seriesIndex]);
                    }
                },
                dataPointSelection(event, chartContext, config) {
                    self.onLegendClick(self.preparedLabels[config.dataPointIndex]);
                },
            };

            // Prevent default legend toggle so cross-filter takes over
            options.legend = {
                ...(options.legend || {}),
                onItemClick: { toggleDataSeries: true },
            };

            this.options = options;

            if(this.mini){
                if(this.options){
                    this.options.legend={
                  show:false
                };
                }

            }
        },
        // Load the async ECharts renderer chunk once. Shared by the chart-engine path AND the count
        // gauge variant — the gauge rides the Apex-resolved count path, so it never trips the
        // chartEngine watcher and needs its own load trigger. On chunk-load failure: mark unavailable
        // (chart widgets fall back to Apex; a gauge falls back to the plain count card) and re-run
        // buildMessage so a chart widget's skipped Apex prep + chartReady cycle still executes.
        ensureEchartsComp() {
            if (this.echartsComp || this.echartsUnavailable) return;
            import('@/features/Dashboard/charts/EChartsWidget.vue')
                .then(m => {
                    this.echartsComp = m.default;
                    this.revealEchartsWhenReady();
                    log('dashboard-widget', 'EChartsWidget chunk loaded', { messageId: this.message?.id });
                })
                .catch(err => {
                    log('dashboard-widget', 'EChartsWidget import failed — apex/count fallback', {
                        err: err && err.message, messageId: this.message?.id,
                    });
                    this.echartsUnavailable = true;
                    this.buildMessage(this.message);
                });
        },
        // First-load reveal for echarts widgets: flip the loader off only once the
        // async renderer is present and there is data, so the existing loader shows
        // during the import (never a blank wrapper). Guarded on !chartReady so it
        // can't fire on re-query (that path must not remount). Sizes the wrapper on
        // the next tick; the child's ResizeObserver then corrects the canvas.
        revealEchartsWhenReady() {
            if (this.chartEngine === 'echarts' && this.echartsComp && this.hasData && !this.chartReady) {
                this.chartReady = true;
                this.$nextTick(() => { this.setChartWrapperSize(); });
            }
        },
        onEchartsSelect(payload) {
            // ECharts reports its ENTIRE post-toggle datum selection — emit the full set
            // through the shared builder (labels: [] clears this widget's selection).
            this.emitSelection((payload && payload.labels) || []);
        }

    },
    watch: {
        message(val) {
            if (val) {
                this.buildMessage(val);
            }
        },
        // hasEverLoaded latches true the first time this widget holds data (immediate so a widget
        // mounted with data reads as "loaded" from first paint). Never resets: it means "has this
        // widget ever shown a successful result", which is what separates an initial load (full
        // skeleton) from a refresh (keep the last render). A new fetch (message change) also clears
        // any prior refresh-failure state.
        hasData: {
            immediate: true,
            handler(has) {
                if (has) this.hasEverLoaded = true;
            },
        },
        // Arm the refresh-with-data scrim only once a genuine refresh has BEGUN (false->true) after
        // mount. Not immediate: an initial refreshing=true is a starting value, not a transition, so
        // it must not arm the latch (see isRefreshWithData / sawRefreshStart).
        refreshing(now, prev) {
            if (now && !prev) this.sawRefreshStart = true;
        },
        // Matrix normalizer outcome logging — kept out of the pure matrixData computed so it fires
        // only on a real state change (mirrors the kpiSchemaOutcome precedent), not every re-render:
        // a config/key error, or a truncation-cap hit.
        matrixData(matrix) {
            if (matrix && matrix.error) {
                log('dashboard-widget', 'matrix config error', {
                    error: matrix.error, messageType: this.message?.messageType, messageId: this.message?.id,
                });
            } else if (matrix && matrix.possiblyTruncated) {
                log('dashboard-widget', 'matrix possibly truncated (row cap hit)', {
                    messageType: this.message?.messageType, messageId: this.message?.id,
                });
            }
        },
        // Guard activation: an ECharts-only type resolved to the Apex fail-safe (no Apex builder).
        echartsOnlyUnavailable(active) {
            if (active) {
                log('dashboard-widget', 'echarts-only fallback guard active', {
                    messageType: this.message?.messageType, messageId: this.message?.id,
                });
            }
        },
        // debugLog on non-matched schema resolves (team-lead §5). Lives in a watch, not the pure
        // kpiSchemaOutcome computed, so it fires only on an actual outcome-state change — not on
        // every count tick / theme flip.
        kpiSchemaOutcome(outcome) {
            if (outcome && outcome.state !== RENDER_STATE.MATCHED) {
                const selection = this.kpiSchemaSelection;
                log('[dashboard-widget] schema resolve non-match',
                    (selection && (selection.schemaId || selection.kind)) || '', outcome.state);
            }
        },
        // Load the ECharts renderer chunk when this widget resolves to echarts.
        // Immediate so a widget that starts as echarts loads on mount. On chunk-load
        // failure (R2-2): mark unavailable (chartEngine then resolves to apex) AND
        // re-run buildMessage so the skipped Apex prep + chartReady cycle executes —
        // the fallback must render a real Apex chart, not a blank widget.
        chartEngine: {
            immediate: true,
            handler(engine) {
                log('dashboard-widget', 'chartEngine resolved', {
                    engine, messageId: this.message?.id, messageType: this.message?.messageType,
                });
                if (engine === 'echarts') this.ensureEchartsComp();
            },
        },
        // Gauge (count-path variant) needs the ECharts chunk too, but the count path resolves to
        // Apex so the chartEngine watcher above never fires for it — load on demand here.
        isGaugeKpi: {
            immediate: true,
            handler(active) {
                if (active) this.ensureEchartsComp();
            },
        },
    }

}
</script>
<style>
.cdb-title-with-actions {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
}
.dashboard-widget-shell {
    position: relative;
    min-width: 0;
    min-height: 0;
    container-type: size;
}
.dashboard-widget-skeleton--overlay {
    position: absolute;
    z-index: 4;
    inset: 0;
    background: var(--ds-surface-elevated, #fcfdfc);
}
/* Refresh scrim (point #8): a restrained translucent veil over the retained render while a
   re-query settles. pointer-events:auto blocks new datum clicks until the data lands; the top
   progress strip carries the "busy" signal so the veil stays light (no heavy dim, no accent bar). */
.cdb-refresh-scrim {
    position: absolute;
    inset: 0;
    z-index: 4;
    background: rgba(252, 253, 252, 0.45);
    pointer-events: auto;
}
.theme--dark .cdb-refresh-scrim {
    background: rgba(24, 28, 27, 0.42);
}
/* Thin indeterminate progress strip pinned to the top edge of the card, above the scrim. */
.cdb-refresh-bar {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 5;
}
/* Stale/error notice (point #8 item 4): a small flat pill when a refresh failed and the last good
   data is retained. Same no-accent-bar language as the cap-sentinel + filter notes; top-centred so
   it clears DashboardItem's top-right overlaid controls. */
.cdb-stale-notice {
    position: absolute;
    top: 4px;
    left: 50%;
    z-index: 5;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    max-width: 92%;
    padding: 1px 4px 1px 9px;
    font-size: 11px;
    line-height: 1.35;
    color: var(--v-text-base);
    background: var(--ds-surface-elevated, #fcfdfc);
    border: 1px solid var(--ds-border-subtle);
    border-radius: var(--ds-radius-full, 999px);
    white-space: nowrap;
    transform: translateX(-50%);
}
.cdb-stale-notice__icon {
    flex: 0 0 auto;
    color: var(--v-text-base);
}
.cdb-stale-notice__text {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
}
.cdb-stale-notice__retry.v-btn {
    height: 20px;
    min-width: 0;
    padding: 0 6px;
}
/* KPI-family empty state (point #1): the value slot shows a neutral no-data dash (never a coerced
   0) with an honest caption; the card reuses the .cdb-kpi2 anatomy from CustomDashboard. Muted ink
   marks it as absent: no tone, no stale schema chip (there is no value to evaluate). */
.cdb-kpi2__num--empty {
    color: var(--v-text-base);
    opacity: 0.5;
}
.cdb-kpi2__empty-caption {
    margin-top: 4px;
    font-family: var(--ds-font-family-body);
    font-size: 12px;
    line-height: 1.35;
    color: var(--v-text-base);
}
.cdb-kpi2__empty-actions {
    margin-top: 6px;
}
.cdb-kpi2__empty-actions .v-btn {
    height: 22px;
}
.cdb-gauge {
    display: flex;
    flex-direction: column;
    height: 100%;
}
.cdb-gauge-wrap {
    width: 100%;
    flex: 1 1 auto;
    min-height: 170px;
}
.chart-wrapper {
    /* max-height: 400px;
    max-width: 400px; */
    position: relative;
    display: flex;
    padding: 8px 0;
    justify-content: center;
    width: 100%;
    height: 100%;
    overflow: hidden;
}
/* ECharts-only fallback + matrix config-error states fill the widget and centre the
   shared .ds-empty-state pattern. */
.cdb-widget-engine-required {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
}
.cdb-matrix-error {
    flex: 1 1 auto;
    height: 100%;
}
/* Cap-sentinel notice: a small, flat, neutral pill pinned to the top of the chart — no accent
   bar, no tinted wash (severity lives in fills only, per the design brief). */
.cdb-matrix-notice {
    position: absolute;
    top: 4px;
    left: 50%;
    z-index: 2;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    max-width: 92%;
    padding: 2px 9px;
    font-size: 11px;
    line-height: 1.35;
    color: var(--v-text-base);
    background: var(--ds-surface-elevated, #fcfdfc);
    border: 1px solid var(--ds-border-subtle);
    border-radius: var(--ds-radius-full, 999px);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    transform: translateX(-50%);
}
.cdb-matrix-notice__icon {
    flex: 0 0 auto;
    color: var(--v-text-base);
}
/* Filter-visibility note (B2): a compact, flat, neutral pill in the widget header cluster —
   same no-accent-bar language as the cap-sentinel notice above. Never a tinted wash; the icon
   + text carry the meaning. Ellipsizes so it stays compact in tight KPI headers (the full
   sentence + reason live in the tooltip). */
.cdb-filter-note {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    min-width: 0;
    max-width: 100%;
    padding: 1px 8px;
    font-size: 11px;
    line-height: 1.3;
    color: var(--v-text-base);
    background: var(--ds-surface-elevated, #fcfdfc);
    border: 1px solid var(--ds-border-subtle);
    border-radius: var(--ds-radius-full, 999px);
    cursor: default;
}
.cdb-filter-note__icon {
    flex: 0 0 auto;
    color: var(--v-text-base);
}
.cdb-filter-note__label {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
/* Title keeps priority; the note shrinks first when the header is tight. */
.cdb-widget-title-group {
    min-width: 0;
    flex: 0 1 auto;
}
.message-wrapper{
    width: 100%;
    height:100%;
    padding-bottom: 10px;
    overflow: hidden;
}
/* Dashboard cards size every widget to the tile; the chat's bottom gap only pushed footers off-line. */
.cdb-builder-v1 .message-wrapper {
    padding-bottom: 0;
}
.message-wrapper--dashboard-grid {
    padding-bottom: 0;
    display: flex;
    flex-direction: column;
    min-height: 0;
}
/* Title row keeps its natural height; the datagrid below flex-fills the remaining card space. */
.message-wrapper--dashboard-grid > .row {
    flex: 0 0 auto;
}
.table-wrapper--dashboard-grid {
    box-sizing: border-box;
    flex: 1 1 auto;
    width: 100%;
    min-height: 0;
    margin: 0;
    overflow: hidden;
    border: 1px solid rgba(27, 52, 45, 0.28);
    border-radius: 6px;
}
.table-wrapper {
    overflow: hidden;
}

.cdb-table-row--interactive {
    cursor: pointer;
}

.cdb-table-row--interactive:focus-visible,
.cdb-table-row--selected {
    outline: 2px solid var(--v-primary-base, #03624c);
    outline-offset: -2px;
    background: rgba(3, 98, 76, 0.08) !important;
}

.cdb-widget-empty {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    min-width: 0;
    min-height: 0;
}
.cdb-widget-empty__head {
    flex: 0 0 auto;
    min-width: 0;
    padding-right: 82px;
}
.cdb-widget-empty__title-row {
    display: flex;
    min-width: 0;
}
.cdb-widget-empty__body {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    min-height: 0;
    padding: 4px 16px 12px;
    text-align: center;
    color: var(--v-text-base);
}
.cdb-widget-empty__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    margin-bottom: 2px;
    border: 1px solid rgba(27, 52, 45, 0.12);
    border-radius: 6px;
    background: rgba(27, 52, 45, 0.035);
}
.cdb-widget-empty__icon .v-icon {
    font-size: 21px;
    color: var(--v-text-base);
}
.cdb-widget-empty__body strong {
    font-family: var(--ds-font-family-body);
    font-size: 13px;
    font-weight: 600;
    line-height: 1.35;
}
.cdb-widget-empty__body p {
    max-width: 46ch;
    margin: 0;
    font-size: 11.5px;
    line-height: 1.45;
    color: var(--v-text-base);
}
.cdb-widget-empty__actions {
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    gap: 4px;
    margin-top: 4px;
}
.theme--dark .cdb-widget-empty__icon {
    border-color: rgba(255, 255, 255, 0.14);
    background: rgba(255, 255, 255, 0.045);
}
.empty-data-section {
    flex-direction: column;
    width: 100%;
    height: 50%;
    opacity: 0.54;
}
.empty-data-section i {
    margin-bottom: 30px;
    font-size: 50px;
}
@container (max-height: 230px) {
    .cdb-widget-empty__body {
        gap: 3px;
        padding: 0 10px 8px;
    }
    .cdb-widget-empty__icon {
        display: none;
    }
    .cdb-widget-empty__body p {
        overflow: hidden;
        max-width: 88%;
        white-space: nowrap;
        text-overflow: ellipsis;
    }
    .cdb-widget-empty__actions {
        margin-top: 1px;
    }
    .cdb-widget-empty__actions .v-btn {
        height: 26px;
        font-size: 10px;
    }
}

.widget-table-small .v-data-table__wrapper table tbody td,
.widget-table-small .v-data-table__wrapper table thead th {
  font-size: 10px !important;
}
.widget-table-medium .nv-data-table__wrapper table tbody td,
.widget-table-medium .v-data-table__wrapper table thead th  {
  font-size: 16px !important;
}
.widget-table-large .v-data-table__wrapper table tbody td,
.widget-table-large .v-data-table__wrapper table thead th {
  font-size: 20px !important;
}
</style>
